// LiteSummaryDiagramReactFlow.js — xHandle look, NO AUTO LAYOUT + ONE-TIME CLEAN & SPREAD
// - Positions persist across unmounts
// - One-time overlap removal + viewport spread after prompt via `cleanOnceKey` prop or `ref.cleanOnce()`
// - Renaming a node preserves its position; label/desc edits do NOT trigger layout

import React, {
  useMemo,
  useCallback,
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import ReactFlow, {
  ReactFlowProvider,
  Background,
  Handle,
  NodeResizer,
  MarkerType,
  Position,
  addEdge,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  BaseEdge,
  StepEdge,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { toPng } from 'html-to-image';
import { SmartBezierEdge } from '@tisoap/react-flow-smart-edge';
import ELK from 'elkjs/lib/elk.bundled.js';
import { downloadDrawioXml } from './utils/exportDrawio';

/* ================================
 * Brand & Theme
 * ================================ */
const BRAND = {
  blue: '#2D7DFE',
  purple: '#7A37FF',
  yellow: '#F3B63F',
  light: '#ECEEFF',
  dark: '#0F0F12',
};

const THEME = {
  radius: 14,
  node: {
    w: 240,
    h: 96,
    pad: 12,
    shadow: '0 6px 20px rgba(24, 29, 54, 0.10)',
    borderAlpha: 0.35,
    titleSize: 14,
  },
  edge: {
    width: 2.5,
    hoverWidth: 3.5,
    opacity: 0.92,
    hoverOpacity: 1,
  },
  canvas: {
    padX: 80,
    padY: 60,
    minZoom: 0.1,
    maxZoom: 1.8,
  },
};

const GROUP = {
  w: 420,
  h: 280,
  minW: 320,
  minH: 180,
  padX: 18,
  padTop: 46,
  padBottom: 18,
  radius: 16,
};

const GROUP_DRAG_EXPAND_MARGIN = 8;
const GROUP_DRAG_EXPAND_STEP = 24;

const NODE_LAYOUT = {
  w: THEME.node.w + THEME.node.pad * 2,
  h: THEME.node.h + THEME.node.pad * 2,
};

const AUTO_CATEGORY = {
  minW: NODE_LAYOUT.w + 72,
  minH: NODE_LAYOUT.h + 80,
  padX: 36,
  padTop: 50,
  padBottom: 30,
  nodeGapX: 260,
  nodeGapY: 120,
  gapX: 190,
  gapY: 160,
};

const NOTE = {
  w: 240,
  h: 160,
};

const COLOR_PRESETS = [
  BRAND.blue,
  BRAND.purple,
  BRAND.yellow,
  '#22C55E',
  '#EF4444',
  '#14B8A6',
];

// Arrow size knob (in px)
const ARROW_SIZE = 18;

const EDGE_ROUTING_STYLES = {
  BEZIER: 'bezier',
  RECTANGULAR: 'rectangular',
};

function normalizeEdgeRoutingStyle(value) {
  return value === EDGE_ROUTING_STYLES.RECTANGULAR
    ? EDGE_ROUTING_STYLES.RECTANGULAR
    : EDGE_ROUTING_STYLES.BEZIER;
}

function edgeTypeForRoutingStyle(value) {
  return normalizeEdgeRoutingStyle(value) === EDGE_ROUTING_STYLES.RECTANGULAR
    ? 'smartStep'
    : 'smartBezier';
}

/* ================================
 * Utilities
 * ================================ */
const rgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function positionsAbsMapFromRF(rfNodes) {
  const m = new Map();
  (rfNodes || []).forEach((n) => {
    const p = n.positionAbsolute || n.position || { x: 0, y: 0 };
    m.set(n.id, { x: p.x, y: p.y });
  });
  return m;
}

function downloadJson(payload, filename) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function cleanNodeForExport(node) {
  return {
    id: node.id,
    type: node.type || 'default',
    label: node.data?.label || node.id,
    description: node.data?.description || '',
    position: node.position || { x: 0, y: 0 },
    positionAbsolute: node.positionAbsolute || node.position || { x: 0, y: 0 },
    parentNode: node.parentNode || null,
    extent: node.extent || null,
    width: node.width || null,
    height: node.height || null,
    style: node.style || null,
    data: {
      brandColor: node.data?.brandColor || null,
      brandTint: node.data?.brandTint || null,
    },
  };
}

function cleanEdgeForExport(edge) {
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceHandle || null,
    targetHandle: edge.targetHandle || null,
    label: edge.label || edge.data?.label || '',
    type: edge.type || 'default',
    animated: !!edge.animated,
    style: edge.style || null,
    markerStart: edge.markerStart || null,
    markerEnd: edge.markerEnd || null,
    data: edge.data || null,
  };
}

/* ================================
 * Occupied spots from existing edges
 * ================================ */
function getOccupiedSpotsFromEdges(edges) {
  const occ = new Set();
  edges.forEach((e) => {
    const s = parseHandleId(e.sourceHandle);
    const t = parseHandleId(e.targetHandle);
    if (s) occ.add(spotKey(e.source, s.side, s.idx));
    if (t) occ.add(spotKey(e.target, t.side, t.idx));
  });
  return occ;
}

/* ================================
 * Prevent overlapping node placement
 * ================================ */
const GRID = 16;
const GAP = 24;

const snap = (v) => Math.round(v / GRID) * GRID;

function isOverlapping(a, b) {
  const w = THEME.node.w, h = THEME.node.h;
  return (
    Math.abs((a.x + w / 2) - (b.x + w / 2)) < (w + GAP) / 2 &&
    Math.abs((a.y + h / 2) - (b.y + h / 2)) < (h + GAP) / 2
  );
}

function nearestFreePosition({ x, y }, existing) {
  let r = 0, step = GRID;
  const maxRings = 50;
  const base = { x: snap(x), y: snap(y) };
  const taken = existing.map((n) => ({ x: n.position.x, y: n.position.y }));
  if (!taken.some((p) => isOverlapping(p, base))) return base;
  for (let ring = 1; ring <= maxRings; ring++) {
    r += step;
    const dirs = [[1, 0], [0, 1], [-1, 0], [0, -1]];
    for (const [dx, dy] of dirs) {
      const candidate = { x: snap(base.x + dx * r), y: snap(base.y + dy * r) };
      if (!taken.some((p) => isOverlapping(p, candidate))) return candidate;
    }
  }
  return base;
}

/* ================================
 * Nudge node if it overlaps after drag
 * ================================ */
function nudgeIfOverlapping(movedId, nodes, setNodes) {
  setNodes((nds) => {
    const me = nds.find((n) => n.id === movedId);
    if (!me) return nds;
    let pos = { ...me.position };
    const others = nds.filter((n) => n.id !== movedId);
    let tries = 0;
    while (others.some((o) => isOverlapping(pos, o.position)) && tries < 60) {
      pos.x = snap(pos.x + (tries % 2 ? GRID : 0));
      pos.y = snap(pos.y + (tries % 2 ? 0 : GRID));
      tries++;
    }
    return nds.map((n) => (n.id === movedId ? { ...n, position: pos } : n));
  });
}

/* ================================
 * ELK one-time layout helpers
 * ================================ */
const elk = new ELK();
const ELK_DEFAULTS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.spacing.nodeNode': '200',
  'elk.layered.spacing.nodeNodeBetweenLayers': '300',
  'elk.padding': '[top=40,left=60,bottom=40,right=60]',
  'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
  'elk.edgeRouting': 'ORTHOGONAL',
};

function toElkGraph({ nodes, edges }) {
  return {
    id: 'root',
    layoutOptions: ELK_DEFAULTS,
    children: nodes.map((n) => ({
      id: n.id,
      width: THEME.node.w,
      height: THEME.node.h,
    })),
    edges: edges.map((e) => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    })),
  };
}

async function runElkLayoutOnce({ nodes, edges }) {
  if (!nodes.length) return nodes;
  const graph = toElkGraph({ nodes, edges });
  const laidOut = await elk.layout(graph);
  const posById = new Map(
    laidOut.children.map((c) => [c.id, { x: Math.round(c.x ?? 0), y: Math.round(c.y ?? 0) }])
  );
  return nodes.map((n) => ({
    ...n,
    position: posById.get(n.id) ?? n.position,
  }));
}

/* ===== Persisted positions helpers (localStorage) ===== */
function loadPositions(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return new Map();
    const arr = JSON.parse(raw);
    return new Map(
      Array.isArray(arr)
        ? arr.map(([id, value]) => {
            if (value && typeof value === 'object' && 'position' in value) return [id, value];
            return [id, { position: value, parentId: null }];
          })
        : []
    );
  } catch {
    return new Map();
  }
}
function savePositions(storageKey, posMap) {
  try {
    const arr = Array.from(posMap.entries());
    localStorage.setItem(storageKey, JSON.stringify(arr));
  } catch {}
}

function loadGroupBoxes(storageKey) {
  try {
    const raw = localStorage.getItem(`${storageKey}:groups:v1`);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveGroupBoxes(storageKey, boxes) {
  try {
    localStorage.setItem(`${storageKey}:groups:v1`, JSON.stringify(boxes || []));
  } catch {}
}

function saveAutoCategoryMeta(storageKey, meta) {
  try {
    localStorage.setItem(`${storageKey}:auto-categories:v1`, JSON.stringify(meta || {}));
  } catch {}
}

function getActiveAutoGroupIds(autoCategories, rows, deletedAutoGroupIds = new Set()) {
  if (!Array.isArray(autoCategories?.categories)) return null;
  const activeCategories = normalizeCategories(autoCategories, rows)
    .filter((category, index) => !deletedAutoGroupIds.has(stableAutoCategoryId(category, index)));
  return new Set(activeCategories.map((category, index) => stableAutoCategoryId(category, index)));
}

function pruneGroupBoxesForProject(groupBoxes, autoCategories, rows, deletedAutoGroupIds = new Set()) {
  const boxes = Array.isArray(groupBoxes) ? groupBoxes : [];
  const activeAutoGroupIds = getActiveAutoGroupIds(autoCategories, rows, deletedAutoGroupIds);
  if (!activeAutoGroupIds) return boxes.filter((box) => !box.autoGenerated);
  return boxes.filter((box) => !box.autoGenerated || activeAutoGroupIds.has(box.id));
}

function prunePositionParentsForGroups(posMap, groupBoxes) {
  const validGroupIds = new Set((groupBoxes || []).map((box) => box.id));
  let changed = false;
  posMap.forEach((value, id) => {
    if (!value?.parentId || validGroupIds.has(value.parentId)) return;
    posMap.set(id, { ...value, parentId: null });
    changed = true;
  });
  return changed;
}

function loadDeletedAutoGroupIds(storageKey) {
  try {
    const raw = localStorage.getItem(`${storageKey}:deleted-auto-groups:v1`);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter(Boolean) : []);
  } catch {
    return new Set();
  }
}

function saveDeletedAutoGroupIds(storageKey, deletedIds) {
  try {
    localStorage.setItem(`${storageKey}:deleted-auto-groups:v1`, JSON.stringify(Array.from(deletedIds || [])));
  } catch {}
}

function loadManualNodes(storageKey) {
  try {
    const raw = localStorage.getItem(`${storageKey}:manual:v1`);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveManualNodes(storageKey, manualNodes) {
  try {
    localStorage.setItem(`${storageKey}:manual:v1`, JSON.stringify(manualNodes || []));
  } catch {}
}

function loadEdgeAggregationState(storageKey) {
  try {
    const raw = localStorage.getItem(`${storageKey}:edge-aggregation:v1`);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      aggregateAll: Boolean(parsed?.aggregateAll),
      aggregatedPairs: new Set(Array.isArray(parsed?.aggregatedPairs) ? parsed.aggregatedPairs.filter(Boolean) : []),
      expandedPairs: new Set(Array.isArray(parsed?.expandedPairs) ? parsed.expandedPairs.filter(Boolean) : []),
      aggregateBidirectionalAll: Boolean(parsed?.aggregateBidirectionalAll),
      aggregatedBidirectionalPairs: new Set(Array.isArray(parsed?.aggregatedBidirectionalPairs) ? parsed.aggregatedBidirectionalPairs.filter(Boolean) : []),
      expandedBidirectionalPairs: new Set(Array.isArray(parsed?.expandedBidirectionalPairs) ? parsed.expandedBidirectionalPairs.filter(Boolean) : []),
    };
  } catch {
    return {
      aggregateAll: false,
      aggregatedPairs: new Set(),
      expandedPairs: new Set(),
      aggregateBidirectionalAll: false,
      aggregatedBidirectionalPairs: new Set(),
      expandedBidirectionalPairs: new Set(),
    };
  }
}

function saveEdgeAggregationState(storageKey, state = {}) {
  try {
    localStorage.setItem(`${storageKey}:edge-aggregation:v1`, JSON.stringify({
      aggregateAll: Boolean(state.aggregateAll),
      aggregatedPairs: Array.from(state.aggregatedPairs || []),
      expandedPairs: Array.from(state.expandedPairs || []),
      aggregateBidirectionalAll: Boolean(state.aggregateBidirectionalAll),
      aggregatedBidirectionalPairs: Array.from(state.aggregatedBidirectionalPairs || []),
      expandedBidirectionalPairs: Array.from(state.expandedBidirectionalPairs || []),
    }));
  } catch {}
}

function loadEdgeRoutingState(storageKey) {
  try {
    const raw = localStorage.getItem(`${storageKey}:edge-routing:v1`);
    const parsed = raw ? JSON.parse(raw) : {};
    const overrides = parsed?.overrides && typeof parsed.overrides === 'object' ? parsed.overrides : {};
    return {
      defaultStyle: normalizeEdgeRoutingStyle(parsed?.defaultStyle),
      overrides: Object.fromEntries(
        Object.entries(overrides)
          .filter(([key]) => Boolean(key))
          .map(([key, value]) => [key, normalizeEdgeRoutingStyle(value)])
      ),
    };
  } catch {
    return { defaultStyle: EDGE_ROUTING_STYLES.BEZIER, overrides: {} };
  }
}

function saveEdgeRoutingState(storageKey, state = {}) {
  try {
    localStorage.setItem(`${storageKey}:edge-routing:v1`, JSON.stringify({
      defaultStyle: normalizeEdgeRoutingStyle(state.defaultStyle),
      overrides: state.overrides && typeof state.overrides === 'object' ? state.overrides : {},
    }));
  } catch {}
}

/* ================================
 * Unique label helpers
 * ================================ */
function collectExistingLabels(nodes, rows) {
  const s = new Set();
  nodes.forEach((n) => s.add(n?.data?.label ?? ''));
  rows.forEach((r) => {
    const from = rowFromFunction(r);
    const to = rowToFunction(r);
    if (from) s.add(from);
    if (to) s.add(to);
  });
  return s;
}

function makeUniqueNewLabel(existingLabels) {
  const prefix = 'new: ';
  let max = 0;
  for (const lab of existingLabels) {
    if (typeof lab === 'string' && lab.startsWith(prefix)) {
      const n = Number(lab.slice(prefix.length).trim());
      if (Number.isFinite(n)) max = Math.max(max, n);
    }
  }
  return `${prefix}${max + 1 || 1}`;
}

/* ================================
 * Node (clean, branded)
 * ================================ */
const portBase = {
  width: 10,
  height: 10,
  background: 'rgba(128, 128, 128, 0.3)',
  border: '1px solid rgba(128, 128, 128, 0.4)',
  borderRadius: '50%',
  opacity: 0,
  transition: 'opacity 120ms',
};

const TOP_BOTTOM_PCTS = [10, 30, 50, 70, 90]; // 5 handles
const LEFT_RIGHT_PCTS = [20, 50, 80];

const BidirectionalNode = ({ data, selected }) => {
  const brandColor = data.brandColor || BRAND.blue;
  const tint = data.brandTint || rgba(brandColor, 0.08);
  const border = `1px solid ${rgba(brandColor, THEME.node.borderAlpha)}`;

  return (
    <div
      style={{
        width: THEME.node.w,
        minHeight: THEME.node.h,
        padding: THEME.node.pad,
        border,
        borderRadius: THEME.radius,
        background: tint,
        boxShadow: selected
          ? `0 0 0 5px ${rgba(brandColor, 0.18)}, ${THEME.node.shadow}`
          : THEME.node.shadow,
        position: 'relative',
        transition: 'box-shadow 150ms ease, border-color 150ms ease, transform 120ms ease',
      }}
      className="x-node"
      onMouseEnter={(e) => e.currentTarget.querySelectorAll('.x-port').forEach((h) => (h.style.opacity = 1))}
      onMouseLeave={(e) => e.currentTarget.querySelectorAll('.x-port').forEach((h) => (h.style.opacity = 0))}
    >
      {/* TOP (5) */}
      {TOP_BOTTOM_PCTS.map((p, i) => (
        <React.Fragment key={`top-${i}`}>
          <Handle className="x-port" type="target" position={Position.Top} id={`top-target-${i}`} style={{ ...portBase, left: `${p}%` }} />
          <Handle className="x-port" type="source" position={Position.Top} id={`top-source-${i}`} style={{ ...portBase, left: `${p}%` }} />
        </React.Fragment>
      ))}

      {/* BOTTOM (5) */}
      {TOP_BOTTOM_PCTS.map((p, i) => (
        <React.Fragment key={`bottom-${i}`}>
          <Handle className="x-port" type="target" position={Position.Bottom} id={`bottom-target-${i}`} style={{ ...portBase, left: `${p}%` }} />
          <Handle className="x-port" type="source" position={Position.Bottom} id={`bottom-source-${i}`} style={{ ...portBase, left: `${p}%` }} />
        </React.Fragment>
      ))}

      {/* LEFT (3) */}
      {LEFT_RIGHT_PCTS.map((p, i) => (
        <React.Fragment key={`left-${i}`}>
          <Handle className="x-port" type="target" position={Position.Left} id={`left-target-${i}`} style={{ ...portBase, top: `${p}%` }} />
          <Handle className="x-port" type="source" position={Position.Left} id={`left-source-${i}`} style={{ ...portBase, top: `${p}%` }} />
        </React.Fragment>
      ))}

      {/* RIGHT (3) */}
      {LEFT_RIGHT_PCTS.map((p, i) => (
        <React.Fragment key={`right-${i}`}>
          <Handle className="x-port" type="target" position={Position.Right} id={`right-target-${i}`} style={{ ...portBase, top: `${p}%` }} />
          <Handle className="x-port" type="source" position={Position.Right} id={`right-source-${i}`} style={{ ...portBase, top: `${p}%` }} />
        </React.Fragment>
      ))}

      {/* Title */}
      <div
        style={{
          fontWeight: 700,
          fontSize: THEME.node.titleSize,
          color: BRAND.dark,
          textAlign: 'center',
          lineHeight: 1.2,
          wordBreak: 'break-word',
          minHeight: THEME.node.h - THEME.node.pad * 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 4,
        }}
      >
        {data.label}
      </div>
    </div>
  );
};

const GroupBoxNode = ({ data, selected }) => {
  const brandColor = data.brandColor || BRAND.purple;
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        border: `2px dashed ${rgba(brandColor, selected ? 0.85 : 0.5)}`,
        background: rgba(brandColor, 0.06),
        borderRadius: GROUP.radius,
        boxShadow: selected ? `0 0 0 5px ${rgba(brandColor, 0.15)}` : 'none',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        pointerEvents: 'none',
      }}
    >
      <NodeResizer
        minWidth={GROUP.minW}
        minHeight={GROUP.minH}
        onResizeStart={(_, params) => data.onResizeStart?.(params)}
        onResize={(_, params) => data.onResize?.(params)}
        onResizeEnd={(_, params) => data.onResizeEnd?.(params)}
        lineStyle={{ borderColor: rgba(brandColor, 0.45), borderWidth: 1, pointerEvents: 'auto' }}
        handleStyle={{
          width: 10,
          height: 10,
          borderRadius: 3,
          border: `1px solid ${rgba(brandColor, 0.65)}`,
          background: '#fff',
          pointerEvents: 'auto',
        }}
        isVisible={selected}
      />
      <div
        className="project-group-drag-handle"
        style={{
          padding: '12px 14px',
          fontSize: 13,
          fontWeight: 700,
          color: BRAND.dark,
          background: rgba(brandColor, 0.08),
          borderBottom: `1px dashed ${rgba(brandColor, 0.3)}`,
          pointerEvents: 'auto',
          cursor: 'grab',
        }}
      >
        {data.label || 'Group'}
      </div>
    </div>
  );
};

const NoteNode = ({ data, selected }) => {
  const brandColor = data.brandColor || BRAND.yellow;
  const tint = data.brandTint || rgba(brandColor, 0.2);
  return (
    <div
      style={{
        width: NOTE.w,
        minHeight: NOTE.h,
        padding: 14,
        border: `1px solid ${rgba(brandColor, 0.45)}`,
        borderRadius: 8,
        background: tint,
        boxShadow: selected ? `0 0 0 5px ${rgba(brandColor, 0.18)}, ${THEME.node.shadow}` : THEME.node.shadow,
        color: BRAND.dark,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.2, wordBreak: 'break-word' }}>
        {data.label || 'Note'}
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {data.description || 'Add a note'}
      </div>
    </div>
  );
};

/* ================================
 * Edge fallback (orthogonal)
 * ================================ */
function OrthogonalFallbackEdge(props) {
  const { id, sourceX, sourceY, targetX, targetY, markerStart, markerEnd, style, selected } = props;
  const viaX = { x: targetX, y: sourceY };
  const useHV = Math.abs(targetX - sourceX) > Math.abs(targetY - sourceY);
  const d = useHV
    ? `M ${sourceX},${sourceY} L ${viaX.x},${viaX.y} L ${targetX},${targetY}`
    : `M ${sourceX},${sourceY} L ${sourceX},${targetY} L ${targetX},${targetY}`;
  const stroke = style?.stroke || '#1f2544';
  const width = selected ? 3.5 : 2.5;
  return (
    <BaseEdge
      id={id}
      path={d}
      markerStart={markerStart}
      markerEnd={{ ...(markerEnd || {}), color: stroke, width: ARROW_SIZE, height: ARROW_SIZE, type: MarkerType.ArrowClosed }}
      style={{ stroke, strokeWidth: width }}
    />
  );
}

/* ================================
 * Handle assignment (use all 4 sides)
 * ================================ */
function handleAnchorsForNode(kind /* 'source' | 'target' */) {
  const W = THEME.node.w;
  const H = THEME.node.h;
  const anchors = [];
  TOP_BOTTOM_PCTS.forEach((p, i) => {
    const x = (p / 100) * W;
    anchors.push({ id: `top-${kind}-${i}`, side: 'top', x, y: 0, idx: i });
    anchors.push({ id: `bottom-${kind}-${i}`, side: 'bottom', x, y: H, idx: i });
  });
  LEFT_RIGHT_PCTS.forEach((p, i) => {
    const y = (p / 100) * H;
    anchors.push({ id: `left-${kind}-${i}`, side: 'left', x: 0, y, idx: i });
    anchors.push({ id: `right-${kind}-${i}`, side: 'right', x: THEME.node.w, y, idx: i });
  });
  return anchors;
}
const spotKey = (nodeId, side, idx) => `${nodeId}:${side}:${idx}`;
function parseHandleId(handleId) {
  const m = /^(top|bottom|left|right)-(?:source|target)-(\d+)$/.exec(handleId || '');
  if (!m) return null;
  return { side: m[1], idx: Number(m[2]) };
}
function normalizeFunctionName(value) {
  return String(value || '').trim();
}
function rowValue(row, keys) {
  for (const key of keys) {
    const value = row?.[key];
    if (String(value || '').trim()) return String(value).trim();
  }
  return '';
}
function rowFromFunction(row) {
  return rowValue(row, ['fromFunction', 'from', 'Function (From)', 'From Function', 'Source Function', 'Controller']);
}
function rowToFunction(row) {
  return rowValue(row, ['toFunction', 'to', 'Function (To)', 'To Function', 'Target Function', 'Controlled Process']);
}
function rowControlAction(row) {
  return rowValue(row, ['controlAction', 'action', 'Control Action']);
}
function rowFromDetails(row) {
  return rowValue(row, ['fromDetails', 'fromFunctionDetails', 'Function (From) Details', 'From Function Details']);
}
function rowToDetails(row) {
  return rowValue(row, ['toDetails', 'toFunctionDetails', 'Function (To) Details', 'To Function Details']);
}
function rowControlDetails(row) {
  return rowValue(row, ['controlDetails', 'controlActionDetails', 'Control Action Details']);
}
function nodeIdForFunction(value) {
  const name = normalizeFunctionName(value);
  return name ? `n:${name}` : null;
}
function edgeIdForRow(row, index) {
  const fromId = nodeIdForFunction(rowFromFunction(row));
  const toId = nodeIdForFunction(rowToFunction(row));
  return fromId && toId ? `e:${fromId}->${toId}-${index}` : null;
}
const sideSlotCount = (side) =>
  (side === 'top' || side === 'bottom') ? TOP_BOTTOM_PCTS.length : LEFT_RIGHT_PCTS.length;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const middleIdxForSide = (side) => Math.floor(sideSlotCount(side) / 2);

// centered order: [mid, mid-1, mid+1, mid-2, mid+2, ...]
function centeredIndexOrder(side) {
  const mid = middleIdxForSide(side);
  const count = sideSlotCount(side);
  const seq = [mid];
  for (let o = 1; o < count; o++) {
    if (mid - o >= 0) seq.push(mid - o);
    if (mid + o < count) seq.push(mid + o);
  }
  return seq;
}

// get anchor x,y in node-local coords for a given side+idx
function anchorXY(side, idx) {
  if (side === 'left')  return { x: 0, y: (LEFT_RIGHT_PCTS[idx] / 100) * THEME.node.h };
  if (side === 'right') return { x: THEME.node.w, y: (LEFT_RIGHT_PCTS[idx] / 100) * THEME.node.h };
  if (side === 'top')   return { x: (TOP_BOTTOM_PCTS[idx] / 100) * THEME.node.w, y: 0 };
  // bottom
  return { x: (TOP_BOTTOM_PCTS[idx] / 100) * THEME.node.w, y: THEME.node.h };
}

function assignHandles(fromId, toId, positions, occupiedSpots, _edgeLabel, edgeIndex = 0, pairIdx = 0) {
  const from = positions.get(fromId);
  const to = positions.get(toId);
  if (!from || !to) return ['right-source-0', 'left-target-0'];

  // 1) Rank side-pairs by middle-to-middle distance
  const sides = ['top', 'right', 'bottom', 'left'];
  const sidePairs = [];
  for (const sSide of sides) {
    const sMid = middleIdxForSide(sSide);
    const sPt = anchorXY(sSide, sMid);
    const sAbs = { x: from.x + sPt.x, y: from.y + sPt.y };

    for (const tSide of sides) {
      const tMid = middleIdxForSide(tSide);
      const tPt = anchorXY(tSide, tMid);
      const tAbs = { x: to.x + tPt.x, y: to.y + tPt.y };
      const dx = tAbs.x - sAbs.x;
      const dy = tAbs.y - sAbs.y;
      sidePairs.push({ sSide, tSide, d2: dx * dx + dy * dy });
    }
  }
  sidePairs.sort((a, b) => a.d2 - b.d2);

  // 2) For each side-pair (closest first), try the middle then expand outward
  for (const { sSide, tSide } of sidePairs) {
    const sOrder = centeredIndexOrder(sSide);
    const tOrder = centeredIndexOrder(tSide);

    // spread parallel edges: skip first N centered options
    const sSeq = sOrder.slice(Math.min(pairIdx, sOrder.length));
    const tSeq = tOrder.slice(Math.min(pairIdx, tOrder.length));

    // If slice made them empty (rare), fall back to full order
    const sTrySeq = sSeq.length ? sSeq : sOrder;
    const tTrySeq = tSeq.length ? tSeq : tOrder;

    for (const si of sTrySeq) {
      const sSpot = spotKey(fromId, sSide, si);
      if (occupiedSpots.has(sSpot)) continue;

      for (const ti of tTrySeq) {
        const tSpot = spotKey(toId, tSide, ti);
        if (occupiedSpots.has(tSpot)) continue;

        // Found closest-to-middle available pair on the closest side-pair
        return [`${sSide}-source-${si}`, `${tSide}-target-${ti}`];
      }
    }
  }

  // 3) Fallback: if everything above is occupied, revert to shortest distance among free handles
  const srcAnchors = handleAnchorsForNode('source');
  const tgtAnchors = handleAnchorsForNode('target');
  let best = null;

  for (let i = 0; i < srcAnchors.length; i++) {
    const sa = srcAnchors[i];
    const sSpot = spotKey(fromId, sa.side, sa.idx);
    if (occupiedSpots.has(sSpot)) continue;
    const sAbsX = from.x + sa.x, sAbsY = from.y + sa.y;

    for (let j = 0; j < tgtAnchors.length; j++) {
      const ta = tgtAnchors[j];
      const tSpot = spotKey(toId, ta.side, ta.idx);
      if (occupiedSpots.has(tSpot)) continue;
      const tAbsX = to.x + ta.x, tAbsY = to.y + ta.y;
      const dx = tAbsX - sAbsX, dy = tAbsY - sAbsY;
      const d2 = dx * dx + dy * dy;

      if (
        best === null ||
        d2 < best.d2 ||
        (d2 === best.d2 && ((i + j + edgeIndex) % 2) === 0)
      ) {
        best = { sSide: sa.side, sIdx: sa.idx, tSide: ta.side, tIdx: ta.idx, d2 };
      }
    }
  }

  if (!best) return ['right-source-0', 'left-target-0'];
  return [`${best.sSide}-source-${best.sIdx}`, `${best.tSide}-target-${best.tIdx}`];
}


/* ================================
 * Edge building (no layout dependency)
 * ================================ */
function edgePairKey(source, target) {
  return `${source || ''}->${target || ''}`;
}

function edgeBidirectionalPairKey(source, target) {
  return [source || '', target || ''].sort().join('<->');
}

function aggregateEdgeIdForPair(pairKey) {
  return `e:aggregate:${pairKey}`;
}

function aggregateEdgeIdForBidirectionalPair(pairKey) {
  return `e:aggregate:bidirectional:${pairKey}`;
}

function shouldAggregatePair(pairKey, count, options = {}) {
  if (count <= 1) return false;
  if (options.aggregateAll) return !options.expandedPairs?.has(pairKey);
  return Boolean(options.aggregatedPairs?.has(pairKey));
}

function shouldAggregateBidirectionalPair(pairKey, count, options = {}) {
  if (count <= 1) return false;
  if (options.aggregateBidirectionalAll) return !options.expandedBidirectionalPairs?.has(pairKey);
  return Boolean(options.aggregatedBidirectionalPairs?.has(pairKey));
}

function summarizeEdgeLabels(edges = []) {
  const labels = Array.from(new Set(edges.map((edge) => String(edge.label || '').trim()).filter(Boolean)));
  if (!labels.length) return '';
  const preview = labels.slice(0, 3).join(', ');
  return labels.length > 3 ? `${preview} +${labels.length - 3}` : preview;
}

function edgeRoutingTargetKey(edge) {
  if (!edge) return '';
  if (edge.data?.bidirectionalAggregated && edge.data?.bidirectionalPairKey) return `bipair:${edge.data.bidirectionalPairKey}`;
  if (edge.data?.aggregated && edge.data?.pairKey) return `pair:${edge.data.pairKey}`;
  return edge.id || '';
}

function resolveEdgeRoutingStyle(edge, routing = {}) {
  const targetKey = edgeRoutingTargetKey(edge);
  const override = targetKey ? routing.overrides?.[targetKey] : null;
  return normalizeEdgeRoutingStyle(override || routing.defaultStyle);
}

function buildEdgesFromRaw(rawEdges, positions, aggregation = {}, routing = {}) {
  const bidirectionalGrouped = new Map();
  rawEdges.forEach((edge, rowIndex) => {
    const pairKey = edgeBidirectionalPairKey(edge.source, edge.target);
    if (!bidirectionalGrouped.has(pairKey)) bidirectionalGrouped.set(pairKey, []);
    bidirectionalGrouped.get(pairKey).push({ ...edge, rowIndex });
  });

  const bidirectionalDisplayEdges = [];
  bidirectionalGrouped.forEach((pairEdges, pairKey) => {
    if (shouldAggregateBidirectionalPair(pairKey, pairEdges.length, aggregation)) {
      const first = pairEdges[0];
      bidirectionalDisplayEdges.push({
        ...first,
        id: aggregateEdgeIdForBidirectionalPair(pairKey),
        label: `${pairEdges.length} bidirectional edges`,
        sourceHandle: null,
        targetHandle: null,
        updatable: false,
        data: {
          ...(first.data || {}),
          aggregated: true,
          bidirectionalAggregated: true,
          bidirectionalPairKey: pairKey,
          count: pairEdges.length,
          summary: summarizeEdgeLabels(pairEdges),
          edgeIds: pairEdges.map((edge) => edge.id),
          rowIndexes: pairEdges.map((edge) => edge.rowIndex),
        },
      });
      return;
    }
    bidirectionalDisplayEdges.push(...pairEdges);
  });

  const grouped = new Map();
  bidirectionalDisplayEdges.forEach((edge) => {
    const pairKey = edgePairKey(edge.source, edge.target);
    if (!grouped.has(pairKey)) grouped.set(pairKey, []);
    grouped.get(pairKey).push(edge);
  });

  const displayEdges = [];
  grouped.forEach((pairEdges, pairKey) => {
    if (shouldAggregatePair(pairKey, pairEdges.length, aggregation)) {
      const first = pairEdges[0];
      displayEdges.push({
        ...first,
        id: aggregateEdgeIdForPair(pairKey),
        label: `${pairEdges.length} edges`,
        sourceHandle: null,
        targetHandle: null,
        updatable: false,
        data: {
          ...(first.data || {}),
          aggregated: true,
          pairKey,
          count: pairEdges.length,
          summary: summarizeEdgeLabels(pairEdges),
          edgeIds: pairEdges.map((edge) => edge.id),
          rowIndexes: pairEdges.map((edge) => edge.rowIndex),
        },
      });
      return;
    }
    displayEdges.push(...pairEdges);
  });

  const occupiedSpots = new Set();
  const pairSeq = new Map();
  return displayEdges.map((e, i) => {
    const key = edgePairKey(e.source, e.target);
    const pairIdx = pairSeq.get(key) || 0;
    pairSeq.set(key, pairIdx + 1);

    const savedSourceHandle = parseHandleId(e.sourceHandle) ? e.sourceHandle : null;
    const savedTargetHandle = parseHandleId(e.targetHandle) ? e.targetHandle : null;
    const [autoSourceHandle, autoTargetHandle] = assignHandles(
      e.source,
      e.target,
      positions,
      occupiedSpots,
      e.label || '',
      i,
      pairIdx
    );
    const sourceHandle = savedSourceHandle || autoSourceHandle;
    const targetHandle = savedTargetHandle || autoTargetHandle;

    const sParsed = parseHandleId(sourceHandle);
    const tParsed = parseHandleId(targetHandle);
    if (sParsed) occupiedSpots.add(spotKey(e.source, sParsed.side, sParsed.idx));
    if (tParsed) occupiedSpots.add(spotKey(e.target, tParsed.side, tParsed.idx));

    const isAggregated = Boolean(e.data?.aggregated);
    const isBidirectionalAggregated = Boolean(e.data?.bidirectionalAggregated);
    const stroke = isAggregated ? BRAND.purple : BRAND.blue;
    const routingTargetKey = edgeRoutingTargetKey(e);
    const routingStyle = resolveEdgeRoutingStyle(e, routing);
    return {
      ...e,
      type: edgeTypeForRoutingStyle(routingStyle),
      sourceHandle,
      targetHandle,
      data: {
        ...(e.data || {}),
        routingStyle,
        routingTargetKey,
      },
      style: {
        stroke,
        strokeWidth: isAggregated ? 5 : 3,
        strokeDasharray: isAggregated ? '8 5' : undefined,
      },
      markerStart: isBidirectionalAggregated
        ? { type: MarkerType.ArrowClosed, color: stroke, width: ARROW_SIZE, height: ARROW_SIZE }
        : undefined,
      markerEnd: { type: MarkerType.ArrowClosed, color: stroke, width: ARROW_SIZE, height: ARROW_SIZE },
      label: isAggregated && e.data?.summary ? `${e.label}: ${e.data.summary}` : e.label,
    };
  });
}

function getNodeAbsolutePosition(node, byId) {
  if (!node) return { x: 0, y: 0 };
  if (!node.parentNode) return { x: node.position.x, y: node.position.y };
  const parent = byId.get(node.parentNode);
  if (!parent) return { x: node.position.x, y: node.position.y };
  const parentAbs = getNodeAbsolutePosition(parent, byId);
  return { x: parentAbs.x + node.position.x, y: parentAbs.y + node.position.y };
}

function buildAbsolutePositionMap(nodes) {
  const byId = new Map((nodes || []).map((n) => [n.id, n]));
  const map = new Map();
  (nodes || []).forEach((node) => {
    map.set(node.id, getNodeAbsolutePosition(node, byId));
  });
  return map;
}

function clampToGroup(position, box) {
  const maxX = Math.max(GROUP.padX, (box?.width || GROUP.w) - NODE_LAYOUT.w - GROUP.padX);
  const maxY = Math.max(GROUP.padTop, (box?.height || GROUP.h) - NODE_LAYOUT.h - GROUP.padBottom);
  return {
    x: Math.min(Math.max(GROUP.padX, position.x), maxX),
    y: Math.min(Math.max(GROUP.padTop, position.y), maxY),
  };
}

function sizeGroupToFitChildren(box, children) {
  const widthBase = Math.max(box?.width || GROUP.w, GROUP.minW);
  const heightBase = Math.max(box?.height || GROUP.h, GROUP.minH);
  if (!children?.length) {
    return { width: widthBase, height: heightBase };
  }

  let maxRight = GROUP.padX + NODE_LAYOUT.w;
  let maxBottom = GROUP.padTop + NODE_LAYOUT.h;
  children.forEach((child) => {
    const x = child?.position?.x ?? GROUP.padX;
    const y = child?.position?.y ?? GROUP.padTop;
    maxRight = Math.max(maxRight, x + NODE_LAYOUT.w + GROUP.padX);
    maxBottom = Math.max(maxBottom, y + NODE_LAYOUT.h + GROUP.padBottom);
  });

  return {
    width: Math.max(widthBase, snap(maxRight)),
    height: Math.max(heightBase, snap(maxBottom)),
  };
}

function rectsOverlap(a, b, padding = GAP) {
  if (!a || !b) return false;
  return (
    a.x < b.x + b.width + padding &&
    a.x + a.width + padding > b.x &&
    a.y < b.y + b.height + padding &&
    a.y + a.height + padding > b.y
  );
}

function nodeRectFromPosition(position, size = NODE_LAYOUT) {
  return {
    x: position?.x ?? 0,
    y: position?.y ?? 0,
    width: size.w || size.width || THEME.node.w,
    height: size.h || size.height || THEME.node.h,
  };
}

function groupRect(box) {
  return {
    x: box?.position?.x ?? 0,
    y: box?.position?.y ?? 0,
    width: box?.width || GROUP.w,
    height: box?.height || GROUP.h,
  };
}

function groupBoxFromRenderedNode(node) {
  if (!node || node.type !== 'groupBox') return null;
  return {
    id: node.id,
    label: cleanCategoryTitle(node.data?.label || node.label || 'Subsystem'),
    description: String(node.data?.description || '').trim(),
    brandColor: node.data?.brandColor || BRAND.purple,
    position: {
      x: node.position?.x ?? 0,
      y: node.position?.y ?? 0,
    },
    width: Number(node.style?.width) || GROUP.w,
    height: Number(node.style?.height) || GROUP.h,
    autoGenerated: Boolean(node.data?.autoGenerated ?? String(node.id || '').startsWith('g:auto:')),
    descriptionSource: node.data?.descriptionSource,
    descriptionUserEdited: Boolean(node.data?.descriptionUserEdited),
    userResized: Boolean(node.data?.userResized),
  };
}

function recoverGroupBoxesFromRenderedNodes(groupBoxes = [], nodes = []) {
  if (groupBoxes.length) return groupBoxes;
  return (nodes || [])
    .map(groupBoxFromRenderedNode)
    .filter(Boolean);
}

function groupBoxListsEqual(a = [], b = []) {
  if (a.length !== b.length) return false;
  return a.every((box, index) => (
    box.id === b[index]?.id &&
    box.label === b[index]?.label &&
    box.description === b[index]?.description &&
    box.brandColor === b[index]?.brandColor &&
    box.autoGenerated === b[index]?.autoGenerated &&
    box.width === b[index]?.width &&
    box.height === b[index]?.height &&
    box.position?.x === b[index]?.position?.x &&
    box.position?.y === b[index]?.position?.y
  ));
}

function occupiedRectsFromNodesAndGroups(nodes = [], groupBoxes = []) {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const rects = [];
  groupBoxes.forEach((box) => rects.push(groupRect(box)));
  nodes.forEach((node) => {
    if (node.type === 'groupBox') return;
    const abs = getNodeAbsolutePosition(node, byId);
    const size = node.type === 'note'
      ? { w: NOTE.w, h: NOTE.h }
      : NODE_LAYOUT;
    rects.push(nodeRectFromPosition(abs, size));
  });
  return rects;
}

function findOpenCanvasPosition(preferred, occupiedRects = [], size = NODE_LAYOUT) {
  const base = { x: snap(preferred?.x ?? 120), y: snap(preferred?.y ?? 120) };
  const candidateClear = (position) => {
    const rect = nodeRectFromPosition(position, size);
    return !occupiedRects.some((occupied) => rectsOverlap(rect, occupied, GAP));
  };
  if (candidateClear(base)) return base;

  const stepX = snap((size.w || size.width || THEME.node.w) + GAP * 2);
  const stepY = snap((size.h || size.height || THEME.node.h) + GAP * 2);
  for (let ring = 1; ring <= 40; ring++) {
    for (let dx = -ring; dx <= ring; dx++) {
      for (let dy = -ring; dy <= ring; dy++) {
        if (Math.abs(dx) !== ring && Math.abs(dy) !== ring) continue;
        const candidate = {
          x: Math.max(0, snap(base.x + dx * stepX)),
          y: Math.max(0, snap(base.y + dy * stepY)),
        };
        if (candidateClear(candidate)) return candidate;
      }
    }
  }
  return base;
}

function findOpenPositionInGroup(box, childNodes = [], newIndex = 0) {
  const nodeGapX = 28;
  const nodeGapY = 28;
  const stepX = NODE_LAYOUT.w + nodeGapX;
  const stepY = NODE_LAYOUT.h + nodeGapY;
  const existingRects = childNodes.map((node) => nodeRectFromPosition(node.position || { x: GROUP.padX, y: GROUP.padTop }));
  const width = Math.max(box?.width || GROUP.w, GROUP.minW);

  for (let expandedRows = 0; expandedRows <= 24; expandedRows++) {
    const height = Math.max(box?.height || GROUP.h, GROUP.minH) + expandedRows * stepY;
    const maxY = height - GROUP.padBottom - NODE_LAYOUT.h;
    const cols = Math.max(1, Math.floor((width - GROUP.padX * 2 + nodeGapX) / stepX));
    const maxRows = Math.max(1, Math.floor((height - GROUP.padTop - GROUP.padBottom + nodeGapY) / stepY));
    const totalSlots = cols * maxRows;
    for (let offset = 0; offset < totalSlots; offset++) {
      const slot = newIndex + offset;
      const col = slot % cols;
      const row = Math.floor(slot / cols);
      const candidate = {
        x: snap(GROUP.padX + col * stepX),
        y: snap(GROUP.padTop + row * stepY),
      };
      if (candidate.y > maxY) continue;
      const rect = nodeRectFromPosition(candidate);
      if (!existingRects.some((existing) => rectsOverlap(rect, existing, GAP))) {
        return {
          position: clampToGroup(candidate, { ...box, width, height }),
          width,
          height,
        };
      }
    }
  }

  const fallbackRow = childNodes.length + newIndex;
  const height = Math.max(box?.height || GROUP.h, GROUP.padTop + GROUP.padBottom + (fallbackRow + 1) * stepY + NODE_LAYOUT.h);
  return {
    position: clampToGroup({ x: GROUP.padX, y: GROUP.padTop + fallbackRow * stepY }, { ...box, width, height }),
    width,
    height: snap(height),
  };
}

function explicitAutoCategories(autoCategories, rows) {
  if (!Array.isArray(autoCategories?.categories)) return [];
  const allFunctions = getUniqueFunctionsFromRows(rows);
  const exactByLower = new Map(allFunctions.map((fn) => [fn.toLowerCase(), fn]));
  const assigned = new Set();
  return autoCategories.categories
    .map((category, index) => {
      const fnSet = new Set();
      (Array.isArray(category?.rowIndexes) ? category.rowIndexes : []).forEach((rowIndex) => {
        const row = rows[Number(rowIndex)];
        if (!row) return;
        const from = rowFromFunction(row);
        const to = rowToFunction(row);
        if (from) fnSet.add(from);
        if (to) fnSet.add(to);
      });
      (category?.functions || category?.functionNames || []).forEach((fn) => {
        const exact = exactByLower.get(String(fn || '').trim().toLowerCase());
        if (exact) fnSet.add(exact);
      });
      const functions = Array.from(fnSet)
        .filter(Boolean)
        .filter((fn) => {
          const key = fn.toLowerCase();
          if (assigned.has(key)) return false;
          assigned.add(key);
          return true;
        });
      return {
        name: cleanCategoryTitle(category?.name || `Category ${index + 1}`),
        sourceIndex: index,
        functions,
        description: String(category?.description || '').trim(),
      };
    })
    .filter((category) => category.name && category.functions.length)
    .filter((category) => !/\b(unallocated|unassigned|uncategorized|other functions?)\b/i.test(category.name));
}

function buildExplicitAutoGroupAssignmentMap(autoCategories, rows, groupBoxes, deletedAutoGroupIds = new Set()) {
  const groupIds = new Set((groupBoxes || []).map((box) => box.id));
  const assignments = new Map();
  explicitAutoCategories(autoCategories, rows)
    .filter((category, index) => !deletedAutoGroupIds.has(stableAutoCategoryId(category, index)))
    .forEach((category, index) => {
      const groupId = stableAutoCategoryId(category, index);
      if (!groupIds.has(groupId)) return;
      category.functions.forEach((fn) => {
        const nodeId = nodeIdForFunction(fn);
        if (nodeId) assignments.set(nodeId, groupId);
      });
    });
  return assignments;
}

function chooseRelatedPositionForCategory(category, rows, currentNodes) {
  const categoryNodeIds = new Set((category.functions || []).map(nodeIdForFunction).filter(Boolean));
  if (!categoryNodeIds.size) return null;
  const relatedIds = new Set();
  rows.forEach((row) => {
    const fromId = nodeIdForFunction(rowFromFunction(row));
    const toId = nodeIdForFunction(rowToFunction(row));
    if (categoryNodeIds.has(fromId) && toId && !categoryNodeIds.has(toId)) relatedIds.add(toId);
    if (categoryNodeIds.has(toId) && fromId && !categoryNodeIds.has(fromId)) relatedIds.add(fromId);
  });
  const byId = new Map((currentNodes || []).map((node) => [node.id, node]));
  const relatedPositions = Array.from(relatedIds)
    .map((id) => byId.get(id))
    .filter(Boolean)
    .map((node) => getNodeAbsolutePosition(node, byId));
  if (!relatedPositions.length) return null;
  const avg = relatedPositions.reduce((acc, pos) => ({ x: acc.x + pos.x, y: acc.y + pos.y }), { x: 0, y: 0 });
  return {
    x: avg.x / relatedPositions.length + NODE_LAYOUT.w + AUTO_CATEGORY.gapX,
    y: avg.y / relatedPositions.length - GROUP.padTop,
  };
}

function reconcileAutoCategoryBoxesIncremental({ categories, rows, currentBoxes, currentNodes }) {
  const activeIds = new Set(categories.map((category, index) => stableAutoCategoryId(category, index)));
  const keptBoxes = (currentBoxes || []).filter((box) => !box.autoGenerated || activeIds.has(box.id));
  const occupiedRects = occupiedRectsFromNodesAndGroups(currentNodes, keptBoxes);
  const nextBoxes = [...keptBoxes];

  categories.forEach((category, index) => {
    const id = stableAutoCategoryId(category, index);
    if (nextBoxes.some((box) => box.id === id)) return;
    const initial = buildAutoCategoryLayout([category]).boxes[0];
    const preferred = chooseRelatedPositionForCategory(category, rows, currentNodes) || {
      x: 80 + index * (AUTO_CATEGORY.minW + AUTO_CATEGORY.gapX),
      y: 90,
    };
    const position = findOpenCanvasPosition(
      preferred,
      occupiedRects,
      { w: initial.width || GROUP.w, h: initial.height || GROUP.h }
    );
    const box = {
      ...initial,
      id,
      position,
      description: String(category?.description || '').trim() || generateGroupDescription(initial.label, category.functions || [], rows),
      descriptionSource: category?.description ? 'prompt-wizard' : 'diagram-generated',
    };
    nextBoxes.push(box);
    occupiedRects.push(groupRect(box));
  });

  return nextBoxes;
}

function placeMissingFunctionalNodesIncrementally({ missingNodeIds, rows, currentNodes, groupBoxes, posMap, autoCategories, deletedAutoGroupIds }) {
  if (!missingNodeIds.length) return groupBoxes;
  let nextBoxes = [...groupBoxes];
  const explicitGroupByNodeId = buildExplicitAutoGroupAssignmentMap(autoCategories, rows, nextBoxes, deletedAutoGroupIds);
  const currentNodeById = new Map((currentNodes || []).map((node) => [node.id, node]));
  const occupiedCanvasRects = occupiedRectsFromNodesAndGroups(currentNodes, nextBoxes);
  const groupChildren = new Map();
  currentNodes.forEach((node) => {
    if (!node.parentNode || node.type === 'groupBox') return;
    if (!groupChildren.has(node.parentNode)) groupChildren.set(node.parentNode, []);
    groupChildren.get(node.parentNode).push(node);
  });

  missingNodeIds.forEach((id, index) => {
    const groupId = explicitGroupByNodeId.get(id);
    const box = groupId ? nextBoxes.find((entry) => entry.id === groupId) : null;
    if (box) {
      const children = groupChildren.get(groupId) || [];
      const placement = findOpenPositionInGroup(box, children, children.length);
      const nextBox = {
        ...box,
        width: Math.max(box.width || GROUP.w, placement.width),
        height: Math.max(box.height || GROUP.h, placement.height),
      };
      nextBoxes = nextBoxes.map((entry) => (entry.id === groupId ? nextBox : entry));
      const node = {
        id,
        type: 'bidirectional',
        position: placement.position,
        parentNode: groupId,
      };
      children.push(node);
      groupChildren.set(groupId, children);
      posMap.set(id, { position: placement.position, parentId: groupId });
      return;
    }

    const relatedPositions = rows
      .flatMap((row) => {
        const fromId = nodeIdForFunction(rowFromFunction(row));
        const toId = nodeIdForFunction(rowToFunction(row));
        if (fromId === id && toId) return [currentNodeById.get(toId)];
        if (toId === id && fromId) return [currentNodeById.get(fromId)];
        return [];
      })
      .filter(Boolean)
      .map((node) => getNodeAbsolutePosition(node, currentNodeById));
    const preferred = relatedPositions.length
      ? {
          x: relatedPositions[0].x + NODE_LAYOUT.w + 80,
          y: relatedPositions[0].y,
        }
      : seedPosition(currentNodes.length + index);
    const position = findOpenCanvasPosition(preferred, occupiedCanvasRects);
    occupiedCanvasRects.push(nodeRectFromPosition(position));
    posMap.set(id, { position, parentId: null });
  });

  return nextBoxes;
}

function buildFunctionalDiagramRenderState({
  rows,
  currentNodes = [],
  groupBoxes = [],
  posMap,
  autoCategories,
  deletedAutoGroupIds,
  startGroupResize = () => {},
  queueGroupResizeUpdate = () => {},
  endGroupResize = () => {},
}) {
  const wantedNodeIds = buildWantedNodeIdSet(rows);
  const sortedNodeIds = Array.from(wantedNodeIds).sort();
  const existingNodeById = new Map(currentNodes.filter((n) => n.type !== 'groupBox').map((n) => [n.id, n]));
  const missingNodeIds = sortedNodeIds.filter((id) => !posMap.has(id) && !existingNodeById.has(id));
  const layoutGroupBoxes = placeMissingFunctionalNodesIncrementally({
    missingNodeIds,
    rows,
    currentNodes,
    groupBoxes,
    posMap,
    autoCategories,
    deletedAutoGroupIds,
  });

  const groupNodes = layoutGroupBoxes.map((box) => ({
    id: box.id,
    type: 'groupBox',
    position: box.position,
    data: {
      label: box.label,
      description: box.description || '',
      brandColor: box.brandColor || BRAND.purple,
      autoGenerated: Boolean(box.autoGenerated),
      descriptionSource: box.descriptionSource,
      descriptionUserEdited: Boolean(box.descriptionUserEdited),
      userResized: Boolean(box.userResized),
      onResizeStart: (dimensions) => startGroupResize(box.id, dimensions),
      onResize: (dimensions) => queueGroupResizeUpdate(box.id, dimensions),
      onResizeEnd: (dimensions) => endGroupResize(box.id, dimensions),
    },
    zIndex: 0,
    style: {
      width: box.width || GROUP.w,
      height: box.height || GROUP.h,
      zIndex: 0,
      padding: 0,
      border: 'none',
      background: 'transparent',
      boxShadow: 'none',
      pointerEvents: 'none',
    },
    draggable: true,
    selectable: true,
    dragHandle: '.project-group-drag-handle',
    connectable: false,
    focusable: false,
  }));

  const nodeShellStyle = {
    zIndex: 2,
    padding: 0,
    border: 'none',
    background: 'transparent',
    boxShadow: 'none',
  };

  const nextFunctionalNodes = sortedNodeIds.map((id, index) => {
    const saved = posMap.get(id);
    const existing = existingNodeById.get(id);
    let pos = saved?.position || existing?.position;
    const savedParentId = saved?.parentId && layoutGroupBoxes.some((box) => box.id === saved.parentId) ? saved.parentId : null;
    const existingParentId = existing?.parentNode && layoutGroupBoxes.some((box) => box.id === existing.parentNode) ? existing.parentNode : null;
    const parentId = savedParentId || existingParentId;

    if (!pos) {
      pos = seedPosition(index);
      posMap.set(id, { position: pos, parentId });
    } else if (!saved) {
      posMap.set(id, { position: pos, parentId });
    }

    if (existing) {
      return {
        ...existing,
        zIndex: 2,
        position: parentId ? clampToGroup(pos, layoutGroupBoxes.find((box) => box.id === parentId)) : pos,
        parentNode: parentId || undefined,
        extent: parentId ? 'parent' : undefined,
        style: {
          ...(existing.style || {}),
          ...nodeShellStyle,
        },
      };
    }

    const name = id.replace(/^n:/, '');
    return {
      id,
      type: 'bidirectional',
      zIndex: 2,
      position: parentId ? clampToGroup(pos, layoutGroupBoxes.find((box) => box.id === parentId)) : pos,
      parentNode: parentId || undefined,
      extent: parentId ? 'parent' : undefined,
      style: nodeShellStyle,
      data: {
        label: name,
        description: '',
        brandColor: BRAND.blue,
        brandTint: rgba(BRAND.blue, 0.08),
      },
    };
  });

  return {
    wantedNodeIds,
    missingNodeIds,
    layoutGroupBoxes,
    groupNodes,
    functionalNodes: nextFunctionalNodes,
  };
}

function rowsToRawEdges(rows) {
  const raw = [];
  rows.forEach((row, idx) => {
    const fromId = nodeIdForFunction(rowFromFunction(row));
    const toId = nodeIdForFunction(rowToFunction(row));
    if (!fromId || !toId) return;
    raw.push({
      id: edgeIdForRow(row, idx),
      source: fromId,
      target: toId,
      animated: false,
      type: 'smartBezier',
      style: { stroke: BRAND.dark },
      updatable: true,
      markerEnd: { type: MarkerType.ArrowClosed, width: ARROW_SIZE, height: ARROW_SIZE, color: BRAND.blue },
      label: rowControlAction(row),
      sourceHandle: row.sourceHandle || row.edgeSourceHandle || row.handles?.source || null,
      targetHandle: row.targetHandle || row.edgeTargetHandle || row.handles?.target || null,
      data: { offsetIndex: 0, description: rowControlDetails(row) || '' },
    });
  });
  return raw;
}

/* ================================
 * Seed position for new nodes only (not a layout)
 * ================================ */
function seedPosition(index = 0) {
  const baseX = 120;
  const baseY = 120;
  const stepX = THEME.node.w + 60; // Node width + gap
  const stepY = THEME.node.h + 40; // Node height + gap
  const cols = 4; // Number of columns before wrapping
  
  const col = index % cols;
  const row = Math.floor(index / cols);
  
  return { 
    x: baseX + col * stepX, 
    y: baseY + row * stepY 
  };
}

/* ================================
 * Structure signature
 * ================================ */
function structureSignature(rows) {
  const nodes = new Set();
  const edgeCounts = new Map();
  rows.forEach((r) => {
    const fromId = nodeIdForFunction(rowFromFunction(r));
    if (fromId) nodes.add(fromId);
    const toId = nodeIdForFunction(rowToFunction(r));
    if (fromId && toId) {
      nodes.add(toId);
      const key = `${fromId}->${toId}`;
      edgeCounts.set(key, (edgeCounts.get(key) || 0) + 1);
    }
  });
  const nodePart = [...nodes].sort().join('|');
  const edgePart = [...edgeCounts.entries()]
    .sort((a, b) => (a[0] < b[0] ? -1 : 1))
    .map(([k, c]) => `${k}:${c}`)
    .join('|');
  return `${nodePart}::${edgePart}`;
}

function buildWantedNodeIdSet(rows) {
  const wanted = new Set();
  (rows || []).forEach((row) => {
    const fromId = nodeIdForFunction(rowFromFunction(row));
    const toId = nodeIdForFunction(rowToFunction(row));
    if (fromId) wanted.add(fromId);
    if (toId) wanted.add(toId);
  });
  return wanted;
}

function rowCategorySignature(rows) {
  return JSON.stringify((rows || []).map((row) => ({
    fromFunction: rowFromFunction(row),
    fromDetails: rowFromDetails(row),
    controlAction: rowControlAction(row),
    controlDetails: rowControlDetails(row),
    sourceHandle: (row.sourceHandle || '').trim(),
    targetHandle: (row.targetHandle || '').trim(),
    toFunction: rowToFunction(row),
    toDetails: rowToDetails(row),
  })));
}

function getUniqueFunctionsFromRows(rows) {
  const functions = new Set();
  (rows || []).forEach((row) => {
    const from = rowFromFunction(row);
    const to = rowToFunction(row);
    if (from) functions.add(from);
    if (to) functions.add(to);
  });
  return Array.from(functions).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
}

function normalizeCategories(aiPlan, rows) {
  const allFunctions = getUniqueFunctionsFromRows(rows);
  const exactByLower = new Map(allFunctions.map((fn) => [fn.toLowerCase(), fn]));
  const categories = Array.isArray(aiPlan?.categories) ? aiPlan.categories : [];
  const assignedFunctionNames = new Set();

  const normalized = categories
    .map((category, index) => {
      const rowIndexes = Array.isArray(category?.rowIndexes) ? category.rowIndexes : [];
      const fnSet = new Set();

      rowIndexes.forEach((rowIndex) => {
        const row = rows[Number(rowIndex)];
        if (!row) return;
        const from = rowFromFunction(row);
        const to = rowToFunction(row);
        if (from) fnSet.add(from);
        if (to) fnSet.add(to);
      });

      (category?.functions || category?.functionNames || []).forEach((fn) => {
        const exact = exactByLower.get(String(fn || '').trim().toLowerCase());
        if (exact) fnSet.add(exact);
      });

      const functions = Array.from(fnSet)
        .filter(Boolean)
        .filter((fn) => {
          const key = fn.toLowerCase();
          if (assignedFunctionNames.has(key)) return false;
          assignedFunctionNames.add(key);
          return true;
        });
      return {
        name: cleanCategoryTitle(category?.name || `Category ${index + 1}`),
        sourceIndex: index,
        functions,
        description: String(category?.description || '').trim(),
      };
    })
    .filter((category) => category.name && category.functions.length)
    .filter((category) => !/\b(unallocated|unassigned|uncategorized|other functions?)\b/i.test(category.name))
    .slice(0, 10);

  if (!normalized.length && allFunctions.length) {
    normalized.push({ name: 'Functional Architecture', functions: allFunctions });
    return normalized;
  }

  const functionToCategory = new Map();
  normalized.forEach((category) => {
    category.functions = Array.from(new Set(category.functions));
    category.functions.forEach((fn) => functionToCategory.set(fn, category));
  });

  const assigned = new Set(functionToCategory.keys());
  const unassigned = allFunctions.filter((fn) => !assigned.has(fn));
  unassigned.forEach((fn) => {
    const relatedRows = (rows || []).filter((row) =>
      rowFromFunction(row) === fn || rowToFunction(row) === fn
    );
    const relatedCategory = relatedRows
      .flatMap((row) => [rowFromFunction(row), rowToFunction(row)])
      .map((relatedFn) => functionToCategory.get(relatedFn))
      .find(Boolean);
    const targetCategory = relatedCategory || normalized[0];
    targetCategory.functions.push(fn);
    functionToCategory.set(fn, targetCategory);
  });

  normalized.forEach((category) => {
    category.functions = Array.from(new Set(category.functions)).filter(Boolean);
  });

  return normalized;
}

function cleanCategoryTitle(value) {
  const cleaned = String(value || 'Category')
    .replace(/\bcomputer software configuration item\b/gi, '')
    .replace(/\bcsci\b/gi, '')
    .replace(/\s*[-:|/]\s*$/g, '')
    .replace(/^\s*[-:|/]\s*/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return (cleaned || 'Category').slice(0, 60);
}

function generateGroupDescription(label, functions = [], rows = []) {
  const uniqueFunctions = Array.from(new Set(functions.map((fn) => String(fn || '').trim()).filter(Boolean)));
  const functionSet = new Set(uniqueFunctions.map((fn) => fn.toLowerCase()));
  const relatedRows = (rows || []).filter((row) => (
    functionSet.has(rowFromFunction(row).toLowerCase()) ||
    functionSet.has(rowToFunction(row).toLowerCase())
  ));
  const actions = Array.from(
    new Set(
      relatedRows
        .map((row) => rowControlAction(row))
        .filter(Boolean)
    )
  );
  const details = Array.from(
    new Set(
      relatedRows
        .flatMap((row) => [row?.fromDetails, row?.controlDetails, row?.toDetails])
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    )
  );
  const functionPreview = uniqueFunctions.slice(0, 6).join(', ');
  const actionPreview = actions.slice(0, 5).join(', ');
  const detailPreview = details.slice(0, 4).join(' ');
  const functionRemainder = uniqueFunctions.length > 6 ? ` and ${uniqueFunctions.length - 6} more` : '';
  const actionRemainder = actions.length > 5 ? ` and ${actions.length - 5} more` : '';
  const title = cleanCategoryTitle(label || 'Category');

  if (!uniqueFunctions.length) {
    return `${title} groups related functional architecture elements. Add functions to this category to populate its generated description.`;
  }

  if (detailPreview) {
    return `${title} groups ${uniqueFunctions.length} function${uniqueFunctions.length === 1 ? '' : 's'} including ${functionPreview}${functionRemainder}. It covers the responsibilities, exchanged data/control actions, and constraints described by these functions and interfaces: ${detailPreview}`;
  }

  const actionSentence = actions.length
    ? ` It covers ${actions.length} interface/control action${actions.length === 1 ? '' : 's'}: ${actionPreview}${actionRemainder}, and should be reviewed for timing, data/state ownership, and receiver effects.`
    : '';

  return `${title} contains ${uniqueFunctions.length} function${uniqueFunctions.length === 1 ? '' : 's'}: ${functionPreview}${functionRemainder}.${actionSentence}`;
}

function stableAutoCategoryId(category, index) {
  const stableIndex = Number.isFinite(Number(category?.sourceIndex)) ? Number(category.sourceIndex) : index;
  const label = cleanCategoryTitle(category?.name || `Category ${index + 1}`);
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return `g:auto:${stableIndex}:${slug || 'category'}`;
}

function buildAutoCategoryLayout(categories) {
  const gapX = AUTO_CATEGORY.gapX;
  const gapY = AUTO_CATEGORY.gapY;
  const nodeGapX = AUTO_CATEGORY.nodeGapX;
  const nodeGapY = AUTO_CATEGORY.nodeGapY;
  const innerPadX = AUTO_CATEGORY.padX;
  const innerPadTop = AUTO_CATEGORY.padTop;
  const innerPadBottom = AUTO_CATEGORY.padBottom;
  const startX = 80;
  const startY = 90;
  const boxes = [];
  const positionByFunction = new Map();
  const columns = categories.length > 4 ? 3 : 2;
  const planned = categories.map((category, categoryIndex) => {
    const functions = category.functions || [];
    const count = functions.length || 1;
    const innerCols = count <= 1 ? 1 : count <= 4 ? 2 : 3;
    const innerRows = Math.max(1, Math.ceil((functions.length || 1) / innerCols));
    const maxRowCount = Math.min(innerCols, count);
    const contentWidth = maxRowCount * NODE_LAYOUT.w + (maxRowCount - 1) * nodeGapX;
    const width = Math.max(AUTO_CATEGORY.minW, innerPadX * 2 + contentWidth);
    const height = Math.max(AUTO_CATEGORY.minH, innerPadTop + innerPadBottom + innerRows * NODE_LAYOUT.h + (innerRows - 1) * nodeGapY);
    return {
      category,
      categoryIndex,
      functions,
      innerCols,
      innerRows,
      width: snap(width),
      height: snap(height),
    };
  });

  const colWidths = Array.from({ length: columns }, (_, col) =>
    Math.max(
      AUTO_CATEGORY.minW,
      ...planned.filter((item) => item.categoryIndex % columns === col).map((item) => item.width)
    )
  );
  const rowCount = Math.ceil(planned.length / columns);
  const rowHeights = Array.from({ length: rowCount }, (_, row) =>
    Math.max(
      AUTO_CATEGORY.minH,
      ...planned.filter((item) => Math.floor(item.categoryIndex / columns) === row).map((item) => item.height)
    )
  );
  const colX = colWidths.map((_, col) =>
    startX + colWidths.slice(0, col).reduce((sum, width) => sum + width + gapX, 0)
  );
  const rowY = rowHeights.map((_, row) =>
    startY + rowHeights.slice(0, row).reduce((sum, height) => sum + height + gapY, 0)
  );

  planned.forEach(({ category, categoryIndex, functions, innerCols, innerRows, width, height }) => {
    const col = categoryIndex % columns;
    const row = Math.floor(categoryIndex / columns);
    const position = {
      x: colX[col],
      y: rowY[row],
    };
    const label = cleanCategoryTitle(category.name);
    const box = {
      id: stableAutoCategoryId(category, categoryIndex),
      label,
      position,
      width,
      height,
      brandColor: COLOR_PRESETS[categoryIndex % COLOR_PRESETS.length],
      autoGenerated: true,
    };
    boxes.push(box);

    const gridHeight = innerRows * NODE_LAYOUT.h + (innerRows - 1) * nodeGapY;
    const gridStartY = innerPadTop + Math.max(0, (height - innerPadTop - innerPadBottom - gridHeight) / 2);

    functions.forEach((fn, functionIndex) => {
      const fnRow = Math.floor(functionIndex / innerCols);
      const rowStart = fnRow * innerCols;
      const rowCount = Math.min(innerCols, functions.length - rowStart);
      const fnCol = functionIndex - rowStart;
      const rowWidth = rowCount * NODE_LAYOUT.w + (rowCount - 1) * nodeGapX;
      const rowStartX = innerPadX + Math.max(0, (width - innerPadX * 2 - rowWidth) / 2);
      positionByFunction.set(fn, {
        parentId: box.id,
        boxPosition: position,
        position: {
          x: rowStartX + fnCol * (NODE_LAYOUT.w + nodeGapX),
          y: gridStartY + fnRow * (NODE_LAYOUT.h + nodeGapY),
        },
      });
    });
  });

  return { boxes, positionByFunction };
}

function applyCategoryLayoutToPositionMap({ categories, rows, posMap }) {
  const functions = getUniqueFunctionsFromRows(rows);
  const renderableFunctions = new Set(functions);
  const prunedCategories = (categories || [])
    .map((category) => ({
      ...category,
      functions: Array.from(new Set((category.functions || [])
        .map((fn) => String(fn || '').trim())
        .filter((fn) => fn && renderableFunctions.has(fn)))),
    }))
    .filter((category) => category.functions.length);
  if (!prunedCategories.length) return [];
  const { boxes, positionByFunction } = buildAutoCategoryLayout(prunedCategories);
  const functionsByGroupId = new Map(prunedCategories.map((category, index) => [
    stableAutoCategoryId(category, index),
    category.functions || [],
  ]));
  const categoryByGroupId = new Map(prunedCategories.map((category, index) => [
    stableAutoCategoryId(category, index),
    category,
  ]));

  boxes.forEach((box) => {
    const category = categoryByGroupId.get(box.id);
    box.description = String(category?.description || '').trim() || generateGroupDescription(box.label, functionsByGroupId.get(box.id) || [], rows);
    box.descriptionSource = category?.description ? 'prompt-wizard' : 'diagram-generated';
  });

  functions.forEach((fn, index) => {
    const placement = positionByFunction.get(fn);
    const id = nodeIdForFunction(fn);
    if (!id) return;
    if (placement) {
      posMap.set(id, {
        position: placement.position,
        parentId: placement.parentId,
      });
    } else {
      const prev = posMap.get(id);
      posMap.set(id, {
        position: prev?.position || seedPosition(index),
        parentId: null,
      });
    }
  });

  return boxes;
}

function serializeManualNode(node) {
  return {
    id: node.id,
    type: node.type || 'bidirectional',
    position: { ...(node.position || { x: 0, y: 0 }) },
    parentNode: node.parentNode || null,
    connectable: node.connectable !== false,
    data: {
      ...(node.data || {}),
    },
  };
}

/* ================================
 * One-time CLEAN PACK
 * ================================ */
function resolveOverlaps({ nodes, containerW, containerH, gap = 36, passes = 60 }) {
  const W = THEME.node.w;
  const H = THEME.node.h;
  const out = nodes.map((n) => ({ ...n, position: { ...n.position } }));

  const clampNode = (n) => {
    n.position.x = Math.min(Math.max(0, n.position.x), Math.max(0, (containerW || 1) - W));
    n.position.y = Math.min(Math.max(0, n.position.y), Math.max(0, (containerH || 1) - H));
  };

  for (let pass = 0; pass < passes; pass++) {
    let movedAny = false;
    for (let i = 0; i < out.length; i++) {
      for (let j = i + 1; j < out.length; j++) {
        const a = out[i], b = out[j];
        const acx = a.position.x + W / 2, acy = a.position.y + H / 2;
        const bcx = b.position.x + W / 2, bcy = b.position.y + H / 2;

        const overlapX = (W + gap) - Math.abs(acx - bcx);
        const overlapY = (H + gap) - Math.abs(acy - bcy);

        if (overlapX > 0 && overlapY > 0) {
          movedAny = true;
          const signX = acx === bcx ? (Math.random() < 0.5 ? -1 : 1) : Math.sign(acx - bcx);
          const signY = acy === bcy ? (Math.random() < 0.5 ? -1 : 1) : Math.sign(acy - bcy);
          const pushX = (overlapX / 2) * signX;
          const pushY = (overlapY / 2) * signY;

          a.position.x += pushX; a.position.y += pushY;
          b.position.x -= pushX; b.position.y -= pushY;

          clampNode(a); clampNode(b);
        }
      }
    }
    if (!movedAny) break;
  }
  return out;
}

/* ================================
 * Spread cleaned nodes
 * ================================ */
function spreadToViewport({ nodes, containerW, containerH, padX = THEME.canvas.padX, padY = THEME.canvas.padY }) {
  if (!nodes.length) return nodes;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  nodes.forEach((n) => {
    const { x, y } = n.position;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  });

  const domainW = Math.max(1, maxX - minX);
  const domainH = Math.max(1, maxY - minY);

  const availW = Math.max(1, containerW - 2 * padX - THEME.node.w);
  const availH = Math.max(1, containerH - 2 * padY - THEME.node.h);

  const scaleX = availW / domainW;
  const scaleY = availH / domainH;

  return nodes.map((n) => {
    const nx = padX + (n.position.x - minX) * scaleX;
    const ny = padY + (n.position.y - minY) * scaleY;
    return { ...n, position: { x: nx, y: ny } };
  });
}

/* ================================
 * Component
 * ================================ */
const DiagramBody = forwardRef(function DiagramBody(
    { rows = [], onUpdateRows, storageKey = 'diagram:positions:v1', cleanOnceKey = null, onCleanApplied, fitAfterClean = true, autoCategories = null, hazardSummary = null, riskRegister = [], onOpenHazardRow, onOpenSafetyIssue },
    ref
) {
  const nodeTypes = useMemo(() => ({ bidirectional: BidirectionalNode, groupBox: GroupBoxNode, note: NoteNode }), []);

  const [nodes, setNodes, reactflowOnNodesChange] = useNodesState([]);
  const [edges, setEdges, reactflowOnEdgesChange] = useEdgesState([]);
  const hasGroups = useMemo(
    () => nodes.some((n) => n.type === 'groupBox'),
    [nodes]
  );
  const edgeTypes = useMemo(() => {
    const Smart = !hasGroups && typeof SmartBezierEdge === 'function' && SmartBezierEdge;
    return { smartBezier: Smart, smartStep: StepEdge };
  }, [hasGroups]);
  const [highlightedEdgeId, setHighlightedEdgeId] = useState(null);
  const [groupBoxes, setGroupBoxes] = useState(() => loadGroupBoxes(storageKey));
  const [manualNodesStore, setManualNodesStore] = useState(() => loadManualNodes(storageKey));
  const [deletedAutoGroupIds, setDeletedAutoGroupIds] = useState(() => loadDeletedAutoGroupIds(storageKey));
  const [edgeAggregation, setEdgeAggregation] = useState(() => loadEdgeAggregationState(storageKey));
  const [edgeRouting, setEdgeRouting] = useState(() => loadEdgeRoutingState(storageKey));
  const [hydratedStorageKey, setHydratedStorageKey] = useState(storageKey);
  const [selectedNodeIds, setSelectedNodeIds] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [canvasToolbarCollapsed, setCanvasToolbarCollapsed] = useState(false);
  const [activeCanvasToolSection, setActiveCanvasToolSection] = useState('create');
  const storageReady = hydratedStorageKey === storageKey;

  const viewNodes = useMemo(() => {
    const active = edges.find((e) => e.id === highlightedEdgeId);
    const actSet = active ? new Set([active?.source, active?.target]) : null;
    return nodes.map((n) => ({
      ...n,
      zIndex: n.type === 'groupBox' && selectedNodeIds.includes(n.id) ? 4 : n.zIndex,
      style: actSet?.has(n.id) ? { ...(n.style || {}), filter: 'drop-shadow(0 0 14px rgba(122,55,255,0.8))' } : n.style,
    }));
  }, [nodes, edges, highlightedEdgeId, selectedNodeIds]);

  const viewEdges = useMemo(
    () =>
      edges.map((e) => {
        const isOn = e.id === highlightedEdgeId;
        const stroke = e.data?.aggregated ? BRAND.purple : BRAND.blue;
        return {
          ...e,
          animated: isOn,
          style: {
            ...(e.style || {}),
            stroke,
            strokeWidth: isOn ? (e.data?.aggregated ? 6 : 4.5) : (e.data?.aggregated ? 5 : THEME.edge.width),
            opacity: isOn ? 1 : THEME.edge.opacity,
            filter: isOn ? 'drop-shadow(0 0 6px rgba(45,125,254,0.45))' : undefined,
          },
          markerEnd: e.markerEnd ?? { type: MarkerType.ArrowClosed, color: stroke, width: ARROW_SIZE, height: ARROW_SIZE },
        };
      }),
    [edges, highlightedEdgeId]
  );

  const diagramHostRef = useRef(null);
  const nodeIdCounter = useRef(0);
  const [editModal, setEditModal] = useState(null);

  const hazardHeaders = useMemo(() => (
    Array.isArray(hazardSummary?.[0]) ? hazardSummary[0].map((header) => String(header || '')) : []
  ), [hazardSummary]);

  const hazardDataRows = useMemo(() => (
    Array.isArray(hazardSummary) ? hazardSummary.slice(1) : []
  ), [hazardSummary]);

  const hazardTitleIndex = useMemo(() => {
    const exactHazardIdx = hazardHeaders.findIndex((header) => /^hazards?$/i.test(header.trim()));
    if (exactHazardIdx >= 0) return exactHazardIdx;
    const fallbackIdx = hazardHeaders.findIndex((header) =>
      /\bhazard\b|\bfailure mode\b|\brisk\b|\bscenario\b|\bevent\b/i.test(header)
    );
    return fallbackIdx >= 0 ? fallbackIdx : 0;
  }, [hazardHeaders]);

  const getHazardCardTitle = useCallback((cells, sourceIndex) => {
    const value = String(cells?.[hazardTitleIndex] ?? '').trim();
    return value || `Hazard ${sourceIndex + 1}`;
  }, [hazardTitleIndex]);

  const getRiskSourceIndexes = useCallback((risk) => {
    const indexes = Array.isArray(risk?.sourceIndexes) ? risk.sourceIndexes : [];
    const sourceIndex = Number(risk?.sourceIndex);
    return Array.from(new Set([
      ...indexes.map((item) => Number(item)).filter((item) => Number.isFinite(item) && item > 0),
      ...(Number.isFinite(sourceIndex) && sourceIndex > 0 ? [sourceIndex] : []),
    ])).sort((a, b) => a - b);
  }, []);

  const getRiskScore = useCallback((risk) => {
    const likelihood = Number(risk?.likelihood) || 0;
    const severity = Number(risk?.severity) || 0;
    const computedScore = likelihood * severity;
    return computedScore || Number(risk?.score) || 0;
  }, []);

  const getRiskPriority = useCallback((risk, score) => {
    const explicitPriority = String(risk?.priority || '').trim().toUpperCase();
    if (['P0', 'P1', 'P2', 'P3+'].includes(explicitPriority)) return explicitPriority;
    const numeric = Number(score) || getRiskScore(risk);
    if (numeric >= 20) return 'P0';
    if (numeric >= 15) return 'P1';
    if (numeric >= 9) return 'P2';
    return 'P3+';
  }, [getRiskScore]);

  const normalizeAssociationText = useCallback((value) => (
    String(value || '').trim().toLowerCase().replace(/\s+/g, ' ')
  ), []);

  const collectAssociationTerms = useCallback((target) => {
    if (!target) return [];
    const terms = new Set();
    const add = (value) => {
      const normalized = normalizeAssociationText(value);
      if (normalized) terms.add(normalized);
    };
    const isGroupTarget = target.type === 'node' && (target.nodeType === 'groupBox' || String(target.id || '').startsWith('g:'));

    if (isGroupTarget) {
      add(target.label);
      const childNodes = nodes.filter((node) => node.parentNode === target.id && node.type !== 'groupBox');
      childNodes.forEach((node) => add(node?.data?.label || String(node?.id || '').replace(/^n:/, '')));
    } else if (target.type === 'node') {
      add(target.label);
    } else if (target.type === 'edge') {
      add(target.label);
      const aggregatedIndexes = new Set(
        (target.data?.rowIndexes || []).map((idx) => Number(idx)).filter((idx) => Number.isFinite(idx))
      );
      rows.forEach((row, idx) => {
        const edgeId = edgeIdForRow(row, idx);
        if (edgeId === target.id || aggregatedIndexes.has(idx)) {
          add(rowFromFunction(row));
          add(rowControlAction(row));
          add(rowToFunction(row));
        }
      });
    }

    return Array.from(terms);
  }, [nodes, normalizeAssociationText, rows]);

  const getAssociatedHazardRows = useCallback((target) => {
    if (!hazardDataRows.length || !target) return [];

    const matchedIndexes = new Set();
    const targetTerms = collectAssociationTerms(target);
    const isGroupTarget = target.type === 'node' && (target.nodeType === 'groupBox' || String(target.id || '').startsWith('g:'));

    if (isGroupTarget) {
      const childNodes = nodes.filter((node) => node.parentNode === target.id && node.type !== 'groupBox');
      const childLabels = new Set(
        childNodes
          .map((node) => normalizeAssociationText(node?.data?.label || String(node?.id || '').replace(/^n:/, '')))
          .filter(Boolean)
      );

      rows.forEach((row, idx) => {
        const from = normalizeAssociationText(rowFromFunction(row));
        const to = normalizeAssociationText(rowToFunction(row));
        if (childLabels.has(from) || childLabels.has(to)) matchedIndexes.add(idx);
      });
    } else if (target.type === 'node') {
      const label = normalizeAssociationText(target.label);
      if (!label) return [];

      rows.forEach((row, idx) => {
        const from = normalizeAssociationText(rowFromFunction(row));
        const to = normalizeAssociationText(rowToFunction(row));
        if (from === label || to === label) matchedIndexes.add(idx);
      });
    } else if (target.type === 'edge') {
      const aggregatedIndexes = Array.isArray(target.data?.rowIndexes)
        ? target.data.rowIndexes.map((idx) => Number(idx)).filter((idx) => Number.isFinite(idx))
        : [];
      aggregatedIndexes.forEach((idx) => matchedIndexes.add(idx));
      rows.forEach((row, idx) => {
        const edgeId = edgeIdForRow(row, idx);
        if (edgeId === target.id) matchedIndexes.add(idx);
      });
    }

    if (targetTerms.length) {
      hazardDataRows.forEach((cells, idx) => {
        const rowText = normalizeAssociationText((cells || []).join(' '));
        if (targetTerms.some((term) => rowText.includes(term))) matchedIndexes.add(idx);
      });
    }

    const associated = Array.from(matchedIndexes)
      .sort((a, b) => a - b)
      .map((idx) => ({ sourceIndex: idx, cells: hazardDataRows[idx] }))
      .filter((entry) => Array.isArray(entry.cells));

    return associated;
  }, [collectAssociationTerms, hazardDataRows, nodes, normalizeAssociationText, rows]);

  const editHazardRows = useMemo(
    () => getAssociatedHazardRows(editModal),
    [editModal, getAssociatedHazardRows]
  );

  const editSafetyIssues = useMemo(() => {
    if (!Array.isArray(riskRegister) || !riskRegister.length || !editHazardRows.length) return [];
    const hazardSourceIndexes = new Set(editHazardRows.map(({ sourceIndex }) => sourceIndex + 1));
    return riskRegister
      .map((risk) => {
        const score = getRiskScore(risk);
        return {
          ...risk,
          sourceIndexes: getRiskSourceIndexes(risk),
          score,
          priority: getRiskPriority(risk, score),
        };
      })
      .filter((risk) => risk.sourceIndexes.some((sourceIndex) => hazardSourceIndexes.has(sourceIndex)))
      .sort((a, b) => b.score - a.score);
  }, [editHazardRows, getRiskPriority, getRiskScore, getRiskSourceIndexes, riskRegister]);

  const clearBrowserTextSelection = useCallback(() => {
    try {
      window.getSelection?.()?.removeAllRanges?.();
    } catch {}
  }, []);

  // positions map persisted across unmounts
  const posRef = useRef(loadPositions(storageKey));
  const groupSaveTimer = useRef(null);
  const manualSaveTimer = useRef(null);
  const saveTimer = useRef(null);
  const resizeFrameRef = useRef(null);
  const pendingGroupResizeRef = useRef(new Map());
  const groupResizeSessionRef = useRef(new Map());
  const groupBoxesRef = useRef(groupBoxes);
  const deletedAutoGroupIdsRef = useRef(deletedAutoGroupIds);
  const edgeAggregationRef = useRef(edgeAggregation);
  const edgeRoutingRef = useRef(edgeRouting);
  const storageKeyRef = useRef(storageKey);
  const appliedAutoCategoriesRef = useRef(null);
  const groupDragRef = useRef(null);
  const childDragRef = useRef(null);
  const persistSoon = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => savePositions(storageKey, posRef.current), 120);
  }, [storageKey]);
  const persistGroupsSoon = useCallback((nextBoxes) => {
    groupBoxesRef.current = nextBoxes;
    if (groupSaveTimer.current) clearTimeout(groupSaveTimer.current);
    groupSaveTimer.current = setTimeout(() => saveGroupBoxes(storageKey, nextBoxes), 120);
  }, [storageKey]);
  const persistManualSoon = useCallback((nextManualNodes) => {
    if (manualSaveTimer.current) clearTimeout(manualSaveTimer.current);
    manualSaveTimer.current = setTimeout(() => saveManualNodes(storageKey, nextManualNodes), 120);
  }, [storageKey]);

  const builtCountRef = useRef(0);
  const builtOnceRef = useRef(false);
  const structureRef = useRef('');
  const fitAfterInitialBuildRef = useRef(false);

  const flushGroupResizeAndPersistence = useCallback(({ updateState = true } = {}) => {
    if (resizeFrameRef.current) {
      cancelAnimationFrame(resizeFrameRef.current);
      resizeFrameRef.current = null;
    }
    const pending = pendingGroupResizeRef.current;
    if (pending.size) {
      pendingGroupResizeRef.current = new Map();
      const baseBoxes = groupBoxesRef.current || [];
      const nextBoxes = baseBoxes.map((box) => {
        const nextSize = pending.get(box.id);
        if (!nextSize) return box;
        const width = nextSize.width;
        const height = nextSize.height;
        const position = nextSize.position || box.position;
        if (
          width === (box.width || GROUP.w) &&
          height === (box.height || GROUP.h) &&
          position?.x === box.position?.x &&
          position?.y === box.position?.y
        ) return box;
        return { ...box, position, width, height, userResized: true };
      });
      groupBoxesRef.current = nextBoxes;
      if (updateState) setGroupBoxes(nextBoxes);
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (groupSaveTimer.current) clearTimeout(groupSaveTimer.current);
    savePositions(storageKey, posRef.current);
    saveGroupBoxes(storageKey, groupBoxesRef.current);
  }, [storageKey]);

  // track which clean keys have been applied
  const cleanedKeysRef = useRef(new Set());

  // Track the connect drag start
  const connectStartRef = useRef(null);
  const edgeUpdateGestureRef = useRef({ active: false, oldEdgeId: null });
  const suppressedEdgeRemovalIdsRef = useRef(new Set());
  const pendingEdgeUpdateRowsRef = useRef(null);

  const { fitView, project, getNodes, getEdges, getViewport } = useReactFlow();

  useEffect(() => {
    groupBoxesRef.current = groupBoxes;
  }, [groupBoxes]);

  useEffect(() => {
    deletedAutoGroupIdsRef.current = deletedAutoGroupIds;
  }, [deletedAutoGroupIds]);

  useEffect(() => {
    edgeAggregationRef.current = edgeAggregation;
    if (storageKeyRef.current !== storageKey) return;
    saveEdgeAggregationState(storageKey, edgeAggregation);
  }, [edgeAggregation, storageKey]);

  useEffect(() => {
    edgeRoutingRef.current = edgeRouting;
    if (storageKeyRef.current !== storageKey) return;
    saveEdgeRoutingState(storageKey, edgeRouting);
  }, [edgeRouting, storageKey]);

  const getConnectableFunctionName = useCallback((nodeId) => {
    const node = getNodes().find((entry) => entry.id === nodeId) || nodes.find((entry) => entry.id === nodeId);
    if (!node || node.type === 'groupBox' || node.type === 'note') return '';
    if (!String(node.id || '').startsWith('n:')) return '';
    return normalizeFunctionName(node.data?.label || String(node.id).replace(/^n:/, ''));
  }, [getNodes, nodes]);

  const rebuildRenderedEdgesFromRows = useCallback((nextRows, sourceNodes = null) => {
    const graphNodes = sourceNodes || getNodes();
    const rawEdges = rowsToRawEdges(nextRows);
    setEdges(buildEdgesFromRaw(rawEdges, buildAbsolutePositionMap(graphNodes), edgeAggregationRef.current, edgeRoutingRef.current));
  }, [getNodes, setEdges]);

  useEffect(() => {
    storageKeyRef.current = storageKey;
    setHydratedStorageKey(null);
    posRef.current = loadPositions(storageKey);
    const loadedDeletedAutoGroupIds = loadDeletedAutoGroupIds(storageKey);
    deletedAutoGroupIdsRef.current = loadedDeletedAutoGroupIds;
    setDeletedAutoGroupIds(loadedDeletedAutoGroupIds);
    const loadedGroupBoxes = pruneGroupBoxesForProject(
      loadGroupBoxes(storageKey),
      autoCategories,
      rows,
      loadedDeletedAutoGroupIds
    );
    const positionsChanged = prunePositionParentsForGroups(posRef.current, loadedGroupBoxes);
    groupBoxesRef.current = loadedGroupBoxes;
    setGroupBoxes(loadedGroupBoxes);
    saveGroupBoxes(storageKey, loadedGroupBoxes);
    if (positionsChanged) savePositions(storageKey, posRef.current);
    setManualNodesStore(loadManualNodes(storageKey));
    const loadedEdgeAggregation = loadEdgeAggregationState(storageKey);
    edgeAggregationRef.current = loadedEdgeAggregation;
    setEdgeAggregation(loadedEdgeAggregation);
    const loadedEdgeRouting = loadEdgeRoutingState(storageKey);
    edgeRoutingRef.current = loadedEdgeRouting;
    setEdgeRouting(loadedEdgeRouting);
    setNodes([]);
    setEdges([]);
    setSelectedNodeIds([]);
    setContextMenu(null);
    setEditModal(null);
    builtOnceRef.current = false;
    fitAfterInitialBuildRef.current = false;
    structureRef.current = '';
    builtCountRef.current = 0;
    appliedAutoCategoriesRef.current = null;
    setHydratedStorageKey(storageKey);
  }, [storageKey, setEdges, setNodes]);

  // Auto-fit only once after the initial build; row edits should not move the viewport.
useEffect(() => {
  if (!builtOnceRef.current) return; // wait until first build
  if (fitAfterInitialBuildRef.current) return;
  fitAfterInitialBuildRef.current = true;
  const t = setTimeout(() => {
    try {
      fitView({ padding: 0.2, includeHiddenNodes: true });
    } catch {}
  }, 0);
  return () => clearTimeout(t);
}, [nodes.length, fitView]);

  // Auto-fit when the host container size changes
useEffect(() => {
  if (!diagramHostRef.current) return;
  const ro = new ResizeObserver(() => {
    setTimeout(() => {
      try {
        fitView({ padding: 0.2, includeHiddenNodes: true });
      } catch {}
    }, 0);
  });
  ro.observe(diagramHostRef.current);
  return () => ro.disconnect();
}, [fitView]);

useEffect(() => {
  const swallowResizeObserverNoise = (event) => {
    const message = event?.message || event?.reason?.message || '';
    if (
      message.includes('ResizeObserver loop completed with undelivered notifications') ||
      message.includes('ResizeObserver loop limit exceeded')
    ) {
      event.preventDefault?.();
      event.stopImmediatePropagation?.();
    }
  };

  window.addEventListener('error', swallowResizeObserverNoise);
  window.addEventListener('unhandledrejection', swallowResizeObserverNoise);
  return () => {
    window.removeEventListener('error', swallowResizeObserverNoise);
    window.removeEventListener('unhandledrejection', swallowResizeObserverNoise);
  };
}, []);

  function findNodeUnderPointer(evt) {
    const bounds = diagramHostRef.current?.getBoundingClientRect();
    if (!bounds) return null;
    const local = { x: evt.clientX - bounds.left, y: evt.clientY - bounds.top };
    const p = project(local);
    const w = THEME.node.w;
    const h = THEME.node.h;
    const hit = getNodes().find((n) => {
      const nx = n.position.x;
      const ny = n.position.y;
      return p.x >= nx && p.x <= nx + w && p.y >= ny && p.y <= ny + h;
    });
    return hit || null;
  }

  /* Node-hover connect (loose) */
  const onConnectStartLoose = useCallback((_, params) => {
    connectStartRef.current = params || null;
  }, []);

  const onConnectEndLoose = useCallback(
    (evt) => {
      const start = connectStartRef.current;
      connectStartRef.current = null;

      const endedOnHandle = evt.target?.closest?.('.react-flow__handle');
      if (endedOnHandle) return;

      const targetNode = findNodeUnderPointer(evt);
      if (!targetNode || !start?.nodeId) return;
      if (edgeUpdateGestureRef.current.active) return;

      const fromId = start.nodeId;
      const toId = targetNode.id;
      if (fromId === toId) return;
      const fromFunction = getConnectableFunctionName(fromId);
      const toFunction = getConnectableFunctionName(toId);
      if (!fromFunction || !toFunction) return;

      const srcNode = nodes.find((n) => n.id === fromId);
      const tgtNode = nodes.find((n) => n.id === toId);
      if (srcNode?.position) posRef.current.set(fromId, { position: { ...srcNode.position }, parentId: srcNode.parentNode || null });
      if (tgtNode?.position) posRef.current.set(toId, { position: { ...tgtNode.position }, parentId: tgtNode.parentNode || null });
      persistSoon();

      const occupied = getOccupiedSpotsFromEdges(edges);
      const pairIdxForThisPair =
        edges.filter(
          (e) =>
            (e.source === fromId && e.target === toId) ||
            (e.source === toId && e.target === fromId)
        ).length;

      const [autoSourceHandle, autoTargetHandle] = assignHandles(
        fromId,
        toId,
        posRef.current,
        occupied,
        '',
        edges.length,
        pairIdxForThisPair
      );

      const sourceHandle = start.handleId || autoSourceHandle;
      const targetHandle = autoTargetHandle;

      const newEdge = {
        id: `e:${cryptoId()}`,
        source: fromId,
        target: toId,
        sourceHandle,
        targetHandle,
        animated: false,
        type: edgeTypeForRoutingStyle(edgeRoutingRef.current.defaultStyle),
        style: { strokeWidth: 3, stroke: BRAND.blue },
        markerEnd: { type: MarkerType.ArrowClosed, width: ARROW_SIZE, height: ARROW_SIZE, color: BRAND.blue },
      };

      setEdges((eds) => addEdge(newEdge, eds));

      onUpdateRows?.([
        ...rows,
        {
          fromFunction,
          fromDetails: '',
          controlAction: '',
          controlDetails: '',
          toFunction,
          toDetails: '',
          sourceHandle,
          targetHandle,
        },
      ]);
    },
    [getConnectableFunctionName, nodes, edges, rows, onUpdateRows, persistSoon]
  );

  useEffect(() => {
    return () => {
      flushGroupResizeAndPersistence({ updateState: false });
      if (manualSaveTimer.current) clearTimeout(manualSaveTimer.current);
      saveManualNodes(storageKey, manualNodesStore);
    };
  }, [flushGroupResizeAndPersistence, storageKey, manualNodesStore]);

  const queueGroupResizeUpdate = useCallback((id, dimensions) => {
    if (!id || !dimensions) return;
    const rawWidth = Number(dimensions.width);
    const rawHeight = Number(dimensions.height);
    if (!Number.isFinite(rawWidth) || !Number.isFinite(rawHeight)) return;
    const width = Math.max(GROUP.minW, Math.round(rawWidth));
    const height = Math.max(GROUP.minH, Math.round(rawHeight));
    const currentBox = groupBoxesRef.current.find((box) => box.id === id);
    const resizeSession = groupResizeSessionRef.current.get(id);
    const baselineBox = resizeSession?.startBox || currentBox;
    const rawX = Number(dimensions.x);
    const rawY = Number(dimensions.y);
    const position = {
      x: Number.isFinite(rawX) ? Math.round(rawX) : (baselineBox?.position?.x ?? 0),
      y: Number.isFinite(rawY) ? Math.round(rawY) : (baselineBox?.position?.y ?? 0),
    };
    const resizedBox = {
      ...(baselineBox || {}),
      id,
      position,
      width,
      height,
    };

    pendingGroupResizeRef.current.set(id, {
      width,
      height,
      position,
    });
    setNodes((nds) => nds.map((node) => {
      if (node.id === id) {
        return {
          ...node,
          position,
          style: {
            ...(node.style || {}),
            width,
            height,
          },
        };
      }
      if (node.parentNode !== id || node.type === 'groupBox') return node;
      const absolutePosition = resizeSession?.childAbsolutePositions?.get(node.id) || {
        x: (baselineBox?.position?.x ?? 0) + (node.position?.x ?? GROUP.padX),
        y: (baselineBox?.position?.y ?? 0) + (node.position?.y ?? GROUP.padTop),
      };
      const nextPosition = clampToGroup(
        {
          x: absolutePosition.x - position.x,
          y: absolutePosition.y - position.y,
        },
        resizedBox
      );
      posRef.current.set(node.id, { position: nextPosition, parentId: id });
      return {
        ...node,
        position: nextPosition,
      };
    }));

    if (resizeFrameRef.current) return;

    resizeFrameRef.current = requestAnimationFrame(() => {
      flushGroupResizeAndPersistence();
    });
  }, [flushGroupResizeAndPersistence, setNodes]);

  const startGroupResize = useCallback((id, dimensions = {}) => {
    if (!id) return;
    const currentBox = groupBoxesRef.current.find((box) => box.id === id);
    const rawX = Number(dimensions.x);
    const rawY = Number(dimensions.y);
    const rawWidth = Number(dimensions.width);
    const rawHeight = Number(dimensions.height);
    const startBox = {
      ...(currentBox || {}),
      id,
      position: {
        x: Number.isFinite(rawX) ? Math.round(rawX) : (currentBox?.position?.x ?? 0),
        y: Number.isFinite(rawY) ? Math.round(rawY) : (currentBox?.position?.y ?? 0),
      },
      width: Number.isFinite(rawWidth) ? Math.max(GROUP.minW, Math.round(rawWidth)) : (currentBox?.width || GROUP.w),
      height: Number.isFinite(rawHeight) ? Math.max(GROUP.minH, Math.round(rawHeight)) : (currentBox?.height || GROUP.h),
    };
    const childAbsolutePositions = new Map();
    getNodes().forEach((node) => {
      if (node.parentNode !== id || node.type === 'groupBox') return;
      childAbsolutePositions.set(node.id, {
        x: startBox.position.x + (node.position?.x ?? GROUP.padX),
        y: startBox.position.y + (node.position?.y ?? GROUP.padTop),
      });
    });
    groupResizeSessionRef.current.set(id, {
      startBox,
      childAbsolutePositions,
    });
  }, [getNodes]);

  const endGroupResize = useCallback((id, dimensions) => {
    queueGroupResizeUpdate(id, dimensions);
    groupResizeSessionRef.current.delete(id);
    flushGroupResizeAndPersistence();
  }, [flushGroupResizeAndPersistence, queueGroupResizeUpdate]);

  const exportDiagramJson = useCallback(() => {
    const exportedAt = new Date().toISOString();
    const payload = {
      schema: 'xhandle.functional-diagram.v1',
      exportedAt,
      storageKey,
      viewport: typeof getViewport === 'function' ? getViewport() : null,
      functionalDecomposition: rows,
      nodes: getNodes().map(cleanNodeForExport),
      edges: getEdges().map(cleanEdgeForExport),
      groups: groupBoxes,
      manualNodes: manualNodesStore,
      autoCategories,
    };
    downloadJson(payload, `xHandle-diagram-${exportedAt.slice(0, 10)}.json`);
  }, [autoCategories, getEdges, getNodes, getViewport, groupBoxes, manualNodesStore, rows, storageKey]);

  const refreshEdgesFromNodes = useCallback((nextNodes) => {
    const raw = rowsToRawEdges(rows);
    setEdges(buildEdgesFromRaw(raw, positionsAbsMapFromRF(nextNodes), edgeAggregationRef.current, edgeRoutingRef.current));
  }, [rows, setEdges]);

  const growGroupToFitMembers = useCallback((groupId, sourceNodes = null) => {
    if (!groupId) return;
    const currentNodes = sourceNodes || getNodes();
    const childNodes = currentNodes.filter((node) => node.parentNode === groupId && node.type !== 'groupBox');
    setGroupBoxes((currentBoxes) => {
      const existingBox = currentBoxes.find((box) => box.id === groupId);
      if (!existingBox) return currentBoxes;
      if (existingBox.userResized) return currentBoxes;

      const nextSize = sizeGroupToFitChildren(existingBox, childNodes);
      if (nextSize.width === (existingBox.width || GROUP.w) && nextSize.height === (existingBox.height || GROUP.h)) {
        return currentBoxes;
      }

      const nextBoxes = currentBoxes.map((box) => (
        box.id === groupId ? { ...box, ...nextSize } : box
      ));
      persistGroupsSoon(nextBoxes);
      return nextBoxes;
    });
  }, [getNodes, persistGroupsSoon]);

  const expandGroupWhileDraggingChild = useCallback((event, draggedNode) => {
    if (!draggedNode?.parentNode || draggedNode.type === 'groupBox') return;
    const groupId = draggedNode.parentNode;
    const currentBox = groupBoxesRef.current.find((box) => box.id === groupId);
    if (!currentBox) return;

    const position = draggedNode.position || { x: GROUP.padX, y: GROUP.padTop };
    const width = Math.max(currentBox.width || GROUP.w, GROUP.minW);
    const height = Math.max(currentBox.height || GROUP.h, GROUP.minH);
    const rightLimit = width - NODE_LAYOUT.w - GROUP.padX;
    const bottomLimit = height - NODE_LAYOUT.h - GROUP.padBottom;
    const zoom = Math.max(0.1, Number(getViewport()?.zoom) || 1);
    const movementX = Number(event?.movementX ?? event?.nativeEvent?.movementX ?? 0) / zoom;
    const movementY = Number(event?.movementY ?? event?.nativeEvent?.movementY ?? 0) / zoom;
    const lastPosition = childDragRef.current?.nodeId === draggedNode.id
      ? childDragRef.current.position
      : null;
    const deltaX = movementX || (lastPosition ? position.x - lastPosition.x : 0);
    const deltaY = movementY || (lastPosition ? position.y - lastPosition.y : 0);
    const stepX = Math.max(GROUP_DRAG_EXPAND_STEP, Math.ceil(Math.abs(deltaX || GROUP_DRAG_EXPAND_STEP)));
    const stepY = Math.max(GROUP_DRAG_EXPAND_STEP, Math.ceil(Math.abs(deltaY || GROUP_DRAG_EXPAND_STEP)));

    const growLeft = position.x <= GROUP.padX + GROUP_DRAG_EXPAND_MARGIN && deltaX < 0 ? stepX : 0;
    const growRight = position.x >= rightLimit - GROUP_DRAG_EXPAND_MARGIN && deltaX > 0 ? stepX : 0;
    const growTop = position.y <= GROUP.padTop + GROUP_DRAG_EXPAND_MARGIN && deltaY < 0 ? stepY : 0;
    const growBottom = position.y >= bottomLimit - GROUP_DRAG_EXPAND_MARGIN && deltaY > 0 ? stepY : 0;
    if (!growLeft && !growRight && !growTop && !growBottom) {
      childDragRef.current = { nodeId: draggedNode.id, parentId: groupId, position: { ...position } };
      return;
    }

    const nextBox = {
      ...currentBox,
      position: {
        x: currentBox.position.x - growLeft,
        y: currentBox.position.y - growTop,
      },
      width: width + growLeft + growRight,
      height: height + growTop + growBottom,
      userResized: true,
    };

    const nextBoxes = groupBoxesRef.current.map((box) => (
      box.id === groupId ? nextBox : box
    ));
    setGroupBoxes(nextBoxes);
    persistGroupsSoon(nextBoxes);

    let nextNodesSnapshot = null;
    setNodes((nds) => {
      const nextNodes = nds.map((node) => {
        if (node.id === groupId) {
          return {
            ...node,
            position: { ...nextBox.position },
            style: {
              ...(node.style || {}),
              width: nextBox.width,
              height: nextBox.height,
            },
          };
        }
        if (node.parentNode !== groupId || node.type === 'groupBox') return node;
        const currentPosition = node.position || { x: GROUP.padX, y: GROUP.padTop };
        const nextPosition = node.id === draggedNode.id
          ? { ...currentPosition }
          : {
              x: currentPosition.x + growLeft,
              y: currentPosition.y + growTop,
            };
        posRef.current.set(node.id, { position: nextPosition, parentId: groupId });
        return {
          ...node,
          position: nextPosition,
        };
      });
      nextNodesSnapshot = nextNodes;
      return nextNodes;
    });

    childDragRef.current = { nodeId: draggedNode.id, parentId: groupId, position: { ...position } };
    persistSoon();
    setTimeout(() => refreshEdgesFromNodes(nextNodesSnapshot || getNodes()), 0);
  }, [getNodes, getViewport, persistGroupsSoon, persistSoon, refreshEdgesFromNodes, setNodes]);

  const assignNodesToGroup = useCallback((nodeIds, groupId) => {
    const targetBox = groupBoxes.find((box) => box.id === groupId);
    if (!targetBox || !nodeIds?.length) return;

    let nextNodesSnapshot = null;
    setNodes((nds) => {
      const byId = new Map(nds.map((n) => [n.id, n]));
      const nextNodes = nds.map((node) => {
        if (!nodeIds.includes(node.id) || node.type === 'groupBox') return node;
        const abs = getNodeAbsolutePosition(node, byId);
        const relative = clampToGroup(
          { x: abs.x - targetBox.position.x, y: abs.y - targetBox.position.y },
          targetBox
        );
        posRef.current.set(node.id, { position: relative, parentId: groupId });
        return { ...node, parentNode: groupId, position: relative };
      });
      nextNodesSnapshot = nextNodes;
      return nextNodes;
    });
    persistSoon();
    setContextMenu(null);
    setTimeout(() => {
      const latestNodes = nextNodesSnapshot || getNodes();
      growGroupToFitMembers(groupId, latestNodes);
      refreshEdgesFromNodes(latestNodes);
    }, 0);
  }, [getNodes, groupBoxes, growGroupToFitMembers, persistSoon, refreshEdgesFromNodes, setNodes]);

  const ungroupNodes = useCallback((nodeIds) => {
    if (!nodeIds?.length) return;
    setNodes((nds) => {
      const byId = new Map(nds.map((n) => [n.id, n]));
      return nds.map((node) => {
        if (!nodeIds.includes(node.id) || node.type === 'groupBox' || !node.parentNode) return node;
        const abs = getNodeAbsolutePosition(node, byId);
        posRef.current.set(node.id, { position: abs, parentId: null });
        return { ...node, parentNode: undefined, extent: undefined, position: abs };
      });
    });
    persistSoon();
    setContextMenu(null);
    setTimeout(() => refreshEdgesFromNodes(getNodes()), 0);
  }, [getNodes, persistSoon, refreshEdgesFromNodes, setNodes]);

  useEffect(() => {
    if (!storageReady) return;
    const signature = autoCategories?.signature || rowCategorySignature(rows);
    if (!Array.isArray(autoCategories?.categories)) return;
    const categories = autoCategories.categories;
    const explicitCategories = explicitAutoCategories(autoCategories, rows);
    const activeCategories = (explicitCategories.length ? explicitCategories : normalizeCategories({ categories }, rows))
      .filter((category, index) => !deletedAutoGroupIds.has(stableAutoCategoryId(category, index)));
    const activeAutoGroupIds = new Set(activeCategories.map((category, index) => stableAutoCategoryId(category, index)));
    if (groupDragRef.current) return;
    if (groupBoxes.some((box) => box.autoGenerated && !activeAutoGroupIds.has(box.id))) {
      setGroupBoxes((currentBoxes) => {
        const nextBoxes = currentBoxes.filter((box) => !box.autoGenerated || activeAutoGroupIds.has(box.id));
        if (nextBoxes.length !== currentBoxes.length) {
          groupBoxesRef.current = nextBoxes;
          persistGroupsSoon(nextBoxes);
          return nextBoxes;
        }
        return currentBoxes;
      });
      return;
    }
    const wantedNodeIds = buildWantedNodeIdSet(rows);
    const functionalNodes = nodes.filter((node) => wantedNodeIds.has(node.id) && node.type !== 'groupBox');
    const needsAutoCategoryRepair = Boolean(
      categories.length &&
      groupBoxes.length &&
      groupBoxes.every((box) => box.autoGenerated) &&
      functionalNodes.some((node) => (
        !node.parentNode ||
        !groupBoxes.some((box) => box.id === node.parentNode)
      ))
    );
    const savedAutoGroupIds = new Set(groupBoxes.filter((box) => box.autoGenerated).map((box) => box.id));
    const hasCompleteSavedAutoLayout = Boolean(
      activeAutoGroupIds.size &&
      savedAutoGroupIds.size === activeAutoGroupIds.size &&
      Array.from(activeAutoGroupIds).every((id) => savedAutoGroupIds.has(id))
    );
    if (hasCompleteSavedAutoLayout && !needsAutoCategoryRepair) {
      appliedAutoCategoriesRef.current = signature;
      return;
    }
    if (!activeCategories.length || (appliedAutoCategoriesRef.current === signature && !needsAutoCategoryRepair)) return;
    if (groupBoxes.some((box) => !box.autoGenerated)) return;

    const shouldBootstrapLayout = !builtOnceRef.current && !nodes.some((node) => node.type !== 'groupBox' && node.position);
    const boxes = shouldBootstrapLayout
      ? applyCategoryLayoutToPositionMap({
          categories: activeCategories,
          rows,
          posMap: posRef.current,
        })
      : reconcileAutoCategoryBoxesIncremental({
          categories: activeCategories,
          rows,
          currentBoxes: groupBoxes,
          currentNodes: nodes,
        });
    if (!boxes.length) return;

    appliedAutoCategoriesRef.current = signature;
    groupBoxesRef.current = boxes;
    setGroupBoxes(boxes);
    saveGroupBoxes(storageKey, boxes);
    savePositions(storageKey, posRef.current);
    saveAutoCategoryMeta(storageKey, {
      signature,
      categoryCount: boxes.length,
      source: autoCategories?.source || 'wizard',
      generatedAt: autoCategories?.generatedAt || new Date().toISOString(),
    });
    structureRef.current = '';
  }, [autoCategories, rows, groupBoxes, nodes, storageKey, deletedAutoGroupIds, persistGroupsSoon, setGroupBoxes, storageReady]);

  // Extra fit when parent flips cleanOnceKey (used after prompt finishes)
useEffect(() => {
  if (!cleanOnceKey) return;
  const t = setTimeout(() => {
    try {
      fitView({ padding: 0.2, includeHiddenNodes: true });
    } catch {}
  }, 120); // small defer lets RF settle labels/edges
  return () => clearTimeout(t);
}, [cleanOnceKey, fitView]);


  const runCleanAndSpread = useCallback(async () => {
    const categories = Array.isArray(autoCategories?.categories) ? autoCategories.categories : [];
    if (categories.length && groupBoxes.every((box) => box.autoGenerated)) {
      const boxes = applyCategoryLayoutToPositionMap({
        categories: normalizeCategories({ categories }, rows),
        rows,
        posMap: posRef.current,
      });
      if (boxes.length) {
        groupBoxesRef.current = boxes;
        setGroupBoxes(boxes);
        saveGroupBoxes(storageKey, boxes);
        savePositions(storageKey, posRef.current);
        structureRef.current = '';
        persistSoon();
        setTimeout(() => fitView({ padding: 0.2, duration: 600, includeHiddenNodes: true }), 0);
        return;
      }
    }

    const movableNodes = nodes.filter((n) => n.type !== 'groupBox' && !n.parentNode);
    const elkNodes = await runElkLayoutOnce({ nodes: movableNodes, edges });
    setNodes((nds) => {
      const laidById = new Map(elkNodes.map((n) => [n.id, n.position]));
      return nds.map((n) => {
        if (!laidById.has(n.id)) return n;
        return { ...n, position: laidById.get(n.id) };
      });
    });
    elkNodes.forEach((n) => posRef.current.set(n.id, { position: { ...n.position }, parentId: null }));
    persistSoon();
    savePositions(storageKey, posRef.current);
    const rawEdges = rowsToRawEdges(rows);
    const absolutePositions = buildAbsolutePositionMap(
      nodes.map((node) => {
        const match = elkNodes.find((laid) => laid.id === node.id);
        return match ? { ...node, position: match.position } : node;
      })
    );
    setEdges(buildEdgesFromRaw(rawEdges, absolutePositions, edgeAggregationRef.current, edgeRoutingRef.current));
    if (fitAfterClean) setTimeout(() => fitView({ padding: 0.2, duration: 600, includeHiddenNodes: true }), 0);
  }, [autoCategories, groupBoxes, nodes, edges, rows, storageKey, fitAfterClean, fitView, persistSoon]);

  const canvasSpawnPosition = useCallback((offset = { x: 96, y: 108 }) => {
    const local = { x: offset.x, y: offset.y };
    try {
      return project(local);
    } catch {
      return { x: local.x, y: local.y };
    }
  }, [project]);

  const addManualDiagramNode = useCallback(() => {
    const existing = collectExistingLabels(getNodes(), rows);
    const label = makeUniqueNewLabel(existing);
    const id = nodeIdForFunction(label);
    const position = nearestFreePosition(canvasSpawnPosition(), getNodes());
    const newNode = {
      id,
      type: 'bidirectional',
      position,
      data: { label, description: '', brandColor: BRAND.purple, brandTint: rgba(BRAND.purple, 0.08) },
    };
    setNodes((nds) => [...nds, newNode]);
    posRef.current.set(id, { position, parentId: null });
    persistSoon();
  }, [canvasSpawnPosition, getNodes, persistSoon, rows, setNodes]);

  const addNoteNode = useCallback(() => {
    const existingNotes = getNodes().filter((node) => node.type === 'note').length;
    const id = `note:${cryptoId()}`;
    const position = nearestFreePosition(canvasSpawnPosition({ x: 128, y: 128 }), getNodes());
    const noteNode = {
      id,
      type: 'note',
      position,
      data: {
        label: `Note ${existingNotes + 1}`,
        description: '',
        brandColor: BRAND.yellow,
        brandTint: rgba(BRAND.yellow, 0.2),
      },
    };
    setNodes((nds) => [...nds, noteNode]);
    posRef.current.set(id, { position, parentId: null });
    persistSoon();
  }, [canvasSpawnPosition, getNodes, persistSoon, setNodes]);

  const createGroupBox = useCallback(() => {
    const currentNodes = getNodes();
    const selectedNodes = currentNodes.filter((node) => (
      selectedNodeIds.includes(node.id) &&
      node.type !== 'groupBox' &&
      !node.id.startsWith('g:')
    ));
    const groupId = `g:${cryptoId()}`;
    const groupCount = groupBoxes.length + 1;
    let position = canvasSpawnPosition({ x: 96, y: 96 });
    let width = GROUP.w;
    let height = GROUP.h;

    if (selectedNodes.length) {
      const byId = new Map(currentNodes.map((node) => [node.id, node]));
      const bounds = selectedNodes.reduce((acc, node) => {
        const abs = getNodeAbsolutePosition(node, byId);
        return {
          minX: Math.min(acc.minX, abs.x),
          minY: Math.min(acc.minY, abs.y),
          maxX: Math.max(acc.maxX, abs.x + THEME.node.w),
          maxY: Math.max(acc.maxY, abs.y + THEME.node.h),
        };
      }, { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity });
      position = {
        x: Math.max(0, bounds.minX - GROUP.padX),
        y: Math.max(0, bounds.minY - GROUP.padTop),
      };
      width = Math.max(GROUP.minW, bounds.maxX - bounds.minX + GROUP.padX * 2);
      height = Math.max(GROUP.minH, bounds.maxY - bounds.minY + GROUP.padTop + GROUP.padBottom);
    }

    const newBox = {
      id: groupId,
      label: `Group ${groupCount}`,
      description: '',
      position,
      width,
      height,
      brandColor: BRAND.purple,
      userResized: false,
      autoGenerated: false,
    };

    setGroupBoxes((currentBoxes) => {
      const nextBoxes = [...currentBoxes, newBox];
      persistGroupsSoon(nextBoxes);
      return nextBoxes;
    });

    if (selectedNodes.length) {
      const byId = new Map(currentNodes.map((node) => [node.id, node]));
      setNodes((nds) => nds.map((node) => {
        if (!selectedNodeIds.includes(node.id) || node.type === 'groupBox') return node;
        const abs = getNodeAbsolutePosition(node, byId);
        const relative = clampToGroup({ x: abs.x - position.x, y: abs.y - position.y }, newBox);
        posRef.current.set(node.id, { position: relative, parentId: groupId });
        return { ...node, parentNode: groupId, extent: 'parent', position: relative };
      }));
      persistSoon();
      setTimeout(() => refreshEdgesFromNodes(getNodes()), 0);
    }
  }, [canvasSpawnPosition, getNodes, groupBoxes.length, persistGroupsSoon, persistSoon, refreshEdgesFromNodes, selectedNodeIds, setNodes]);

  const ungroupSelectedNodes = useCallback(() => {
    const selected = selectedNodeIds.length ? selectedNodeIds : getNodes().filter((node) => node.parentNode).map((node) => node.id);
    ungroupNodes(selected);
  }, [getNodes, selectedNodeIds, ungroupNodes]);

  const exportDiagramXml = useCallback(() => {
    const n = getNodes();
    const e = getEdges();
    downloadDrawioXml(n, e, 'xHandle-diagram.drawio.xml', {
      pageWidth: 1920,
      pageHeight: 1080,
      nodeSize: { width: 240, height: 96 },
      nodeStyle:
        'rounded=1;whiteSpace=wrap;html=1;strokeColor=#334155;fillColor=#EEF2FF;fontColor=#0F0F12;shadow=0;arcSize=12;spacing=8;',
      edgeStyle:
        'edgeStyle=orthogonalEdgeStyle;rounded=1;html=1;endArrow=block;strokeColor=#94A3B8;strokeWidth=2;',
    });
  }, [getEdges, getNodes]);

  // expose imperative actions to parent
  useImperativeHandle(ref, () => ({
    async exportAsImage() {
      const flowCanvas = diagramHostRef.current?.querySelector('.react-flow');
      if (!flowCanvas) return null;
      return await toPng(flowCanvas, { backgroundColor: '#ffffff', style: { margin: '0 auto', display: 'block' } });
    },
    async getPNG() { return this.exportAsImage(); },
    isReady() { return !!diagramHostRef.current?.querySelector('.react-flow'); },
    cleanOnce() { runCleanAndSpread(); },
    fitViewToDiagram() {
      try { fitView({ padding: 0.2, duration: 400, includeHiddenNodes: true }); } catch {}
    },
    exportJson() {
      exportDiagramJson();
    },
        exportDrawio() {
      exportDiagramXml();
    },
  }));

  const onNodesChange = useCallback(
    (changes) => {
      changes.forEach((c) => {
        if (c.type === 'position' && c.id && c.position) {
          const prev = posRef.current.get(c.id) || { position: c.position, parentId: null };
          posRef.current.set(c.id, { ...prev, position: { ...c.position } });
          persistSoon();
        }
        if (c.type === 'dimensions' && c.id && c.dimensions) {
          const targetBox = groupBoxes.find((box) => box.id === c.id);
          if (targetBox && !groupResizeSessionRef.current.has(c.id)) {
            queueGroupResizeUpdate(c.id, c.dimensions);
          }
        }
        if (c.type === 'remove' && c.id) {
          posRef.current.delete(c.id);
          persistSoon();
        }
      });

      const deletions = changes.filter((cc) => cc.type === 'remove');
      if (deletions.length > 0) {
        const deletedIds = new Set(deletions.map((cc) => cc.id));
        const deletedGroups = groupBoxes.filter((box) => deletedIds.has(box.id));
        if (deletedGroups.length) {
          const deletedAutoIds = deletedGroups
            .filter((box) => box.autoGenerated)
            .map((box) => box.id);
          if (deletedAutoIds.length) {
            setDeletedAutoGroupIds((currentIds) => {
              const nextIds = new Set(currentIds);
              deletedAutoIds.forEach((id) => nextIds.add(id));
              deletedAutoGroupIdsRef.current = nextIds;
              saveDeletedAutoGroupIds(storageKey, nextIds);
              return nextIds;
            });
          }
          setGroupBoxes((currentBoxes) => {
            const nextBoxes = currentBoxes.filter((box) => !deletedIds.has(box.id));
            persistGroupsSoon(nextBoxes);
            return nextBoxes;
          });
          setNodes((nds) => {
            const byId = new Map(nds.map((n) => [n.id, n]));
            return nds.map((n) => {
              if (!n.parentNode || !deletedIds.has(n.parentNode)) return n;
              const abs = getNodeAbsolutePosition(n, byId);
              posRef.current.set(n.id, { position: abs, parentId: null });
              return { ...n, parentNode: undefined, extent: undefined, position: abs };
            });
          });
        }
        const updatedRows = rows.filter((r) => {
          const fromId = nodeIdForFunction(rowFromFunction(r));
          const toId = nodeIdForFunction(rowToFunction(r));
          return !deletedIds.has(fromId) && !deletedIds.has(toId);
        });
        onUpdateRows?.(updatedRows);
      }
      reactflowOnNodesChange(changes);
    },
    [rows, reactflowOnNodesChange, onUpdateRows, persistSoon, groupBoxes, queueGroupResizeUpdate, persistGroupsSoon, setNodes, storageKey]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      const removals = changes
        .filter((c) => c.type === 'remove')
        .map((c) => c.id)
        .filter((id) => !suppressedEdgeRemovalIdsRef.current.has(id));
      if (removals.length) {
        const removalSet = new Set(removals);
        const updatedRows = rows.filter((r, i) => !removalSet.has(edgeIdForRow(r, i)));
        onUpdateRows?.(updatedRows);
      }
      reactflowOnEdgesChange(changes.filter((change) => (
        change.type !== 'remove' || !suppressedEdgeRemovalIdsRef.current.has(change.id)
      )));
    },
    [rows, reactflowOnEdgesChange, onUpdateRows]
  );

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setHighlightedEdgeId(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /* Build when structure changes */
  useEffect(() => {
    if (!storageReady) return;
    let cancelled = false;

    const sig = structureSignature(rows);
    const activeGroupBoxes = recoverGroupBoxesFromRenderedNodes(groupBoxes, nodes);
    const recoveredGroupBoxes = !groupBoxes.length && activeGroupBoxes.length;
    const groupSig = JSON.stringify(activeGroupBoxes.map((box) => [box.id, box.label, box.description, box.brandColor, box.autoGenerated]));
    const fullSig = `${sig}::${groupSig}`;
    const structureUnchanged = builtOnceRef.current && fullSig === structureRef.current;
    if (structureUnchanged) {
      if (recoveredGroupBoxes) {
        groupBoxesRef.current = activeGroupBoxes;
        setGroupBoxes(activeGroupBoxes);
        persistGroupsSoon(activeGroupBoxes);
      }
      return;
    }

    const {
      wantedNodeIds,
      missingNodeIds,
      layoutGroupBoxes,
      groupNodes,
      functionalNodes: nextFunctionalNodes,
    } = buildFunctionalDiagramRenderState({
      rows,
      currentNodes: nodes,
      groupBoxes: activeGroupBoxes,
      posMap: posRef.current,
      autoCategories,
      deletedAutoGroupIds,
      startGroupResize,
      queueGroupResizeUpdate,
      endGroupResize,
    });
    const groupBoxesChanged = !groupBoxListsEqual(layoutGroupBoxes, groupBoxes);
    if (missingNodeIds.length) persistSoon();

    const scopedManualNodes = manualNodesStore.filter((node) => !wantedNodeIds.has(node.id));
    if (scopedManualNodes.length !== manualNodesStore.length) {
      setManualNodesStore(scopedManualNodes);
      persistManualSoon(scopedManualNodes);
    }

    const manualNodes = scopedManualNodes.map((node) => {
      const saved = posRef.current.get(node.id);
      const fallbackPosition = node.position || { x: 0, y: 0 };
      const position = saved?.position || fallbackPosition;
      const parentId =
        saved?.parentId && layoutGroupBoxes.some((box) => box.id === saved.parentId)
          ? saved.parentId
          : node.parentNode && layoutGroupBoxes.some((box) => box.id === node.parentNode)
            ? node.parentNode
            : null;

      if (!saved) {
        posRef.current.set(node.id, { position, parentId });
        persistSoon();
      }

      return {
        ...node,
        zIndex: parentId ? 2 : node.zIndex,
        position: parentId ? clampToGroup(position, layoutGroupBoxes.find((box) => box.id === parentId)) : position,
        parentNode: parentId || undefined,
        extent: parentId ? 'parent' : undefined,
        style: parentId
          ? {
              ...(node.style || {}),
              zIndex: 2,
            }
          : node.style,
      };
    });

    const nextNodes = [...groupNodes, ...nextFunctionalNodes, ...manualNodes];
    const rawEdges = rowsToRawEdges(rows);
    const nextEdges = buildEdgesFromRaw(rawEdges, buildAbsolutePositionMap(nextNodes), edgeAggregationRef.current, edgeRoutingRef.current);

    if (!cancelled) {
      if (groupBoxesChanged) {
        groupBoxesRef.current = layoutGroupBoxes;
        setGroupBoxes(layoutGroupBoxes);
        persistGroupsSoon(layoutGroupBoxes);
      }
      setNodes(nextNodes);
      setEdges(nextEdges);
      builtOnceRef.current = true;
      structureRef.current = fullSig;
    }
    return () => { cancelled = true; };
  }, [rows, persistSoon, nodes, setNodes, setEdges, groupBoxes, startGroupResize, queueGroupResizeUpdate, endGroupResize, storageReady, autoCategories, deletedAutoGroupIds, persistGroupsSoon]);

  useEffect(() => {
    if (!storageReady) return;
    if (!groupBoxes.length) return;
    if (groupDragRef.current) return;

    setGroupBoxes((currentBoxes) => {
      const nextBoxes = currentBoxes.map((box) => {
        if (box.descriptionUserEdited) return box;
        if (box.descriptionSource === 'prompt-wizard' && box.description) return box;
        const childFunctions = nodes
          .filter((node) => node.parentNode === box.id && node.type !== 'groupBox')
          .map((node) => String(node?.data?.label || '').trim())
          .filter(Boolean);
        const generatedDescription = generateGroupDescription(box.label, childFunctions, rows);
        if (box.description === generatedDescription) return box;
        return { ...box, description: generatedDescription };
      });

      const changed = nextBoxes.some((box, index) => box.description !== currentBoxes[index]?.description);
      if (changed) {
        persistGroupsSoon(nextBoxes);
        return nextBoxes;
      }
      return currentBoxes;
    });
  }, [groupBoxes, nodes, rows, persistGroupsSoon, storageReady]);

  useEffect(() => {
    if (!storageReady) return;
    if (!groupBoxes.length || !nodes.length) return;
    if (groupDragRef.current) return;
    setGroupBoxes((currentBoxes) => {
      const nextBoxes = currentBoxes.map((box) => {
        if (box.autoGenerated) return box;
        const childNodes = nodes.filter((node) => node.parentNode === box.id && node.type !== 'groupBox');
        if (box.userResized) return box;
        const nextSize = sizeGroupToFitChildren(box, childNodes);
        if (nextSize.width === (box.width || GROUP.w) && nextSize.height === (box.height || GROUP.h)) {
          return box;
        }
        return { ...box, ...nextSize };
      });

      const changed = nextBoxes.some((box, index) => (
        box.width !== currentBoxes[index]?.width ||
        box.height !== currentBoxes[index]?.height ||
        box.position?.x !== currentBoxes[index]?.position?.x ||
        box.position?.y !== currentBoxes[index]?.position?.y ||
        box.userResized !== currentBoxes[index]?.userResized
      ));

      if (changed) {
        persistGroupsSoon(nextBoxes);
        return nextBoxes;
      }

      return currentBoxes;
    });
  }, [autoCategories, groupBoxes, nodes, rows, persistGroupsSoon, storageReady]);

  useEffect(() => {
    if (!storageReady) return;
    if (!builtOnceRef.current) return;
    const wantedNodeIds = buildWantedNodeIdSet(rows);
    const nextManualNodes = nodes
      .filter((node) => node.type !== 'groupBox' && !wantedNodeIds.has(node.id))
      .map((node) => serializeManualNode(node));

    const prevSig = JSON.stringify(manualNodesStore);
    const nextSig = JSON.stringify(nextManualNodes);
    if (prevSig === nextSig) return;

    setManualNodesStore(nextManualNodes);
    persistManualSoon(nextManualNodes);
  }, [nodes, rows, manualNodesStore, persistManualSoon, storageReady]);

  // Sync labels/details without moving nodes
  useEffect(() => {
    if (!storageReady) return;
    if (!builtOnceRef.current) return;

    const nodeDetails = new Map();
    const edgeDetails = new Map();

    rows.forEach((r, idx) => {
      const fromLabel = normalizeFunctionName(rowFromFunction(r));
      const toLabel = normalizeFunctionName(rowToFunction(r));
      const fromId = nodeIdForFunction(fromLabel);
      const toId = nodeIdForFunction(toLabel);
      if (fromId) nodeDetails.set(fromId, { label: fromLabel, description: rowFromDetails(r) || '' });
      if (toId) {
        nodeDetails.set(toId, { label: toLabel, description: rowToDetails(r) || '' });
        const edgeId = edgeIdForRow(r, idx);
        if (edgeId) edgeDetails.set(edgeId, { label: rowControlAction(r) || '', description: rowControlDetails(r) || '' });
      }
    });

    setNodes((nds) =>
      nds.map((n) => (nodeDetails.has(n.id) ? { ...n, data: { ...n.data, ...nodeDetails.get(n.id) } } : n))
    );
    setEdges((eds) =>
      eds.map((e) =>
        edgeDetails.has(e.id)
          ? { ...e, label: edgeDetails.get(e.id).label, data: { ...e.data, description: edgeDetails.get(e.id).description } }
          : e
      )
    );
  }, [rows, setNodes, setEdges, storageReady]);

  /* One-time clean+spread trigger */
  useEffect(() => {
    if (!storageReady) return;
    if (!cleanOnceKey) return;
    if (cleanedKeysRef.current.has(cleanOnceKey)) return;
    if (!nodes.length) return;

    if (Array.isArray(autoCategories?.categories) && autoCategories.categories.length) {
      setTimeout(() => {
        try { fitView({ padding: 0.2, duration: 600, includeHiddenNodes: true }); } catch {}
      }, 120);
      cleanedKeysRef.current.add(cleanOnceKey);
      try { onCleanApplied?.(cleanOnceKey); } catch {}
      return;
    }

    runCleanAndSpread();
    cleanedKeysRef.current.add(cleanOnceKey);
    // tell parent we consumed the key so it won't fire on remount
    try { onCleanApplied?.(cleanOnceKey); } catch {}
  }, [cleanOnceKey, nodes, autoCategories, fitView, runCleanAndSpread, onCleanApplied, storageReady]);

  /* Connect / Update */
  const onConnect = useCallback(
    (connection) => {
      if (edgeUpdateGestureRef.current.active) return;
      const fromFunction = getConnectableFunctionName(connection.source);
      const toFunction = getConnectableFunctionName(connection.target);
      if (!fromFunction || !toFunction || fromFunction === toFunction) return;
      setEdges((eds) => {
        const count = eds.filter(
          (e) =>
            (e.source === connection.source && e.target === connection.target) ||
            (e.source === connection.target && e.target === connection.source)
        ).length;

        const newEdge = {
          ...connection,
          animated: false,
          type: edgeTypeForRoutingStyle(edgeRoutingRef.current.defaultStyle),
          style: { strokeWidth: 3 },
          markerEnd: { type: MarkerType.ArrowClosed, width: ARROW_SIZE, height: ARROW_SIZE, color: BRAND.blue },
        };

        const srcNode = nodes.find((n) => n.id === connection.source);
        const tgtNode = nodes.find((n) => n.id === connection.target);
        const srcBuiltId = nodeIdForFunction(fromFunction);
        const tgtBuiltId = nodeIdForFunction(toFunction);
        if (srcNode?.position && srcBuiltId) posRef.current.set(srcBuiltId, { position: { ...srcNode.position }, parentId: srcNode.parentNode || null });
        if (tgtNode?.position && tgtBuiltId) posRef.current.set(tgtBuiltId, { position: { ...tgtNode.position }, parentId: tgtNode.parentNode || null });
        persistSoon();

        onUpdateRows?.([
          ...rows,
          {
            fromFunction,
            fromDetails: '',
            controlAction: '',
            controlDetails: '',
            toFunction,
            toDetails: '',
            sourceHandle: connection.sourceHandle || null,
            targetHandle: connection.targetHandle || null,
          },
        ]);

        return addEdge(newEdge, eds);
      });
    },
    [getConnectableFunctionName, setEdges, rows, onUpdateRows, nodes, persistSoon]
  );

  const onEdgeUpdateStart = useCallback((_, edge) => {
    edgeUpdateGestureRef.current = { active: true, oldEdgeId: edge?.id || null };
    if (edge?.id) suppressedEdgeRemovalIdsRef.current.add(edge.id);
  }, []);

  const onEdgeUpdate = useCallback(
    (oldEdge, newConn) => {
      const oldRowIndex = rows.findIndex((row, index) => edgeIdForRow(row, index) === oldEdge.id);
      const fromFunction = getConnectableFunctionName(newConn.source);
      const toFunction = getConnectableFunctionName(newConn.target);
      if (oldEdge?.id) suppressedEdgeRemovalIdsRef.current.add(oldEdge.id);
      if (oldRowIndex < 0 || !fromFunction || !toFunction || fromFunction === toFunction) return;
      if (oldRowIndex >= 0) {
        const nextRows = rows.map((row, index) => (
          index === oldRowIndex
            ? {
                ...row,
                fromFunction,
                toFunction,
                sourceHandle: parseHandleId(newConn.sourceHandle) ? newConn.sourceHandle : (row.sourceHandle || null),
                targetHandle: parseHandleId(newConn.targetHandle) ? newConn.targetHandle : (row.targetHandle || null),
              }
            : row
        ));
        pendingEdgeUpdateRowsRef.current = nextRows;
        onUpdateRows?.(nextRows);
      }
    },
    [getConnectableFunctionName, onUpdateRows, rows]
  );

  const onEdgeUpdateEnd = useCallback(() => {
    const oldEdgeId = edgeUpdateGestureRef.current.oldEdgeId;
    window.setTimeout(() => {
      const nextRows = pendingEdgeUpdateRowsRef.current;
      pendingEdgeUpdateRowsRef.current = null;
      if (nextRows) rebuildRenderedEdgesFromRows(nextRows);
      if (oldEdgeId) suppressedEdgeRemovalIdsRef.current.delete(oldEdgeId);
      edgeUpdateGestureRef.current = { active: false, oldEdgeId: null };
    }, 0);
  }, [rebuildRenderedEdgesFromRows]);

  const canUngroupSelected = selectedNodeIds.some((id) => (
    nodes.some((node) => node.id === id && node.parentNode)
  ));
  const edgePairs = useMemo(() => {
    const pairs = new Map();
    rowsToRawEdges(rows).forEach((edge) => {
      const pairKey = edgePairKey(edge.source, edge.target);
      if (!pairs.has(pairKey)) pairs.set(pairKey, []);
      pairs.get(pairKey).push(edge);
    });
    return pairs;
  }, [rows]);
  const bidirectionalEdgePairs = useMemo(() => {
    const pairs = new Map();
    rowsToRawEdges(rows).forEach((edge) => {
      const pairKey = edgeBidirectionalPairKey(edge.source, edge.target);
      if (!pairs.has(pairKey)) pairs.set(pairKey, []);
      pairs.get(pairKey).push(edge);
    });
    return pairs;
  }, [rows]);
  const multiEdgePairCount = useMemo(
    () => Array.from(edgePairs.values()).filter((pairEdges) => pairEdges.length > 1).length,
    [edgePairs]
  );
  const multiBidirectionalEdgePairCount = useMemo(
    () => Array.from(bidirectionalEdgePairs.values()).filter((pairEdges) => pairEdges.length > 1).length,
    [bidirectionalEdgePairs]
  );
  const highlightedEdge = useMemo(
    () => edges.find((edge) => edge.id === highlightedEdgeId),
    [edges, highlightedEdgeId]
  );
  const highlightedPairKey = highlightedEdge
    ? highlightedEdge.data?.pairKey || edgePairKey(highlightedEdge.source, highlightedEdge.target)
    : '';
  const highlightedBidirectionalPairKey = highlightedEdge
    ? highlightedEdge.data?.bidirectionalPairKey || edgeBidirectionalPairKey(highlightedEdge.source, highlightedEdge.target)
    : '';
  const highlightedPairEdges = highlightedPairKey ? edgePairs.get(highlightedPairKey) || [] : [];
  const highlightedBidirectionalPairEdges = highlightedBidirectionalPairKey ? bidirectionalEdgePairs.get(highlightedBidirectionalPairKey) || [] : [];
  const canToggleHighlightedPair = highlightedPairEdges.length > 1;
  const canToggleHighlightedBidirectionalPair = highlightedBidirectionalPairEdges.length > 1;
  const highlightedPairAggregated = canToggleHighlightedPair
    ? shouldAggregatePair(highlightedPairKey, highlightedPairEdges.length, edgeAggregation)
    : false;
  const highlightedBidirectionalPairAggregated = canToggleHighlightedBidirectionalPair
    ? shouldAggregateBidirectionalPair(highlightedBidirectionalPairKey, highlightedBidirectionalPairEdges.length, edgeAggregation)
    : false;
  const highlightedRoutingTargetKey = highlightedEdge ? edgeRoutingTargetKey(highlightedEdge) : '';
  const highlightedRoutingStyle = highlightedEdge
    ? resolveEdgeRoutingStyle(highlightedEdge, edgeRouting)
    : normalizeEdgeRoutingStyle(edgeRouting.defaultStyle);
  const rebuildEdgesWithRouting = useCallback((nextRouting) => {
    const rawEdges = rowsToRawEdges(rows);
    setEdges(buildEdgesFromRaw(rawEdges, buildAbsolutePositionMap(getNodes()), edgeAggregationRef.current, nextRouting));
  }, [getNodes, rows, setEdges]);
  const applyEdgeAggregation = useCallback((updater) => {
    setEdgeAggregation((current) => {
      const next = updater({
        aggregateAll: Boolean(current.aggregateAll),
        aggregatedPairs: new Set(current.aggregatedPairs || []),
        expandedPairs: new Set(current.expandedPairs || []),
        aggregateBidirectionalAll: Boolean(current.aggregateBidirectionalAll),
        aggregatedBidirectionalPairs: new Set(current.aggregatedBidirectionalPairs || []),
        expandedBidirectionalPairs: new Set(current.expandedBidirectionalPairs || []),
      });
      edgeAggregationRef.current = next;
      const rawEdges = rowsToRawEdges(rows);
      setEdges(buildEdgesFromRaw(rawEdges, buildAbsolutePositionMap(getNodes()), next, edgeRoutingRef.current));
      return next;
    });
  }, [getNodes, rows, setEdges]);
  const toggleAggregateAllEdges = useCallback(() => {
    applyEdgeAggregation((current) => ({
      ...current,
      aggregateAll: !current.aggregateAll,
      aggregatedPairs: new Set(),
      expandedPairs: new Set(),
    }));
    setHighlightedEdgeId(null);
  }, [applyEdgeAggregation]);
  const toggleAggregateAllBidirectionalEdges = useCallback(() => {
    applyEdgeAggregation((current) => ({
      ...current,
      aggregateBidirectionalAll: !current.aggregateBidirectionalAll,
      aggregatedBidirectionalPairs: new Set(),
      expandedBidirectionalPairs: new Set(),
    }));
    setHighlightedEdgeId(null);
  }, [applyEdgeAggregation]);
  const toggleHighlightedEdgePairAggregation = useCallback(() => {
    if (!canToggleHighlightedPair || !highlightedPairKey) return;
    applyEdgeAggregation((current) => {
      if (current.aggregateAll) {
        if (current.expandedPairs.has(highlightedPairKey)) {
          current.expandedPairs.delete(highlightedPairKey);
        } else {
          current.expandedPairs.add(highlightedPairKey);
        }
      } else if (current.aggregatedPairs.has(highlightedPairKey)) {
        current.aggregatedPairs.delete(highlightedPairKey);
      } else {
        current.aggregatedPairs.add(highlightedPairKey);
      }
      return current;
    });
    setHighlightedEdgeId(null);
  }, [applyEdgeAggregation, canToggleHighlightedPair, highlightedPairKey]);
  const toggleHighlightedBidirectionalEdgePairAggregation = useCallback(() => {
    if (!canToggleHighlightedBidirectionalPair || !highlightedBidirectionalPairKey) return;
    applyEdgeAggregation((current) => {
      if (current.aggregateBidirectionalAll) {
        if (current.expandedBidirectionalPairs.has(highlightedBidirectionalPairKey)) {
          current.expandedBidirectionalPairs.delete(highlightedBidirectionalPairKey);
        } else {
          current.expandedBidirectionalPairs.add(highlightedBidirectionalPairKey);
        }
      } else if (current.aggregatedBidirectionalPairs.has(highlightedBidirectionalPairKey)) {
        current.aggregatedBidirectionalPairs.delete(highlightedBidirectionalPairKey);
      } else {
        current.aggregatedBidirectionalPairs.add(highlightedBidirectionalPairKey);
      }
      return current;
    });
    setHighlightedEdgeId(null);
  }, [applyEdgeAggregation, canToggleHighlightedBidirectionalPair, highlightedBidirectionalPairKey]);
  const setAllEdgeRoutingStyle = useCallback((style) => {
    const next = {
      defaultStyle: normalizeEdgeRoutingStyle(style),
      overrides: {},
    };
    edgeRoutingRef.current = next;
    setEdgeRouting(next);
    rebuildEdgesWithRouting(next);
  }, [rebuildEdgesWithRouting]);
  const toggleHighlightedEdgeRoutingStyle = useCallback(() => {
    if (!highlightedRoutingTargetKey) return;
    const nextStyle = highlightedRoutingStyle === EDGE_ROUTING_STYLES.RECTANGULAR
      ? EDGE_ROUTING_STYLES.BEZIER
      : EDGE_ROUTING_STYLES.RECTANGULAR;
    setEdgeRouting((current) => {
      const next = {
        defaultStyle: normalizeEdgeRoutingStyle(current.defaultStyle),
        overrides: {
          ...(current.overrides || {}),
          [highlightedRoutingTargetKey]: nextStyle,
        },
      };
      edgeRoutingRef.current = next;
      rebuildEdgesWithRouting(next);
      return next;
    });
  }, [highlightedRoutingStyle, highlightedRoutingTargetKey, rebuildEdgesWithRouting]);

  const toolSectionLabelStyle = {
    margin: '6px 2px 2px',
    color: 'rgba(15,15,18,0.52)',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0,
    textTransform: 'uppercase',
  };
  const toolGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 32px)',
    gap: 6,
  };
  const toolButtonStyle = ({ active = false, disabled = false, tone = BRAND.blue } = {}) => ({
    width: 32,
    height: 32,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    border: `1px solid ${disabled ? 'rgba(15,15,18,0.12)' : rgba(tone, active ? 0.8 : 0.32)}`,
    background: disabled ? '#F8FAFC' : active ? tone : 'white',
    color: disabled ? 'rgba(15,15,18,0.34)' : active ? 'white' : tone,
    fontWeight: 800,
    fontSize: 13,
    lineHeight: 1,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.62 : 1,
  });
  const toolCategoryButtonStyle = ({ active = false } = {}) => ({
    width: 30,
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    border: `1px solid ${active ? rgba(BRAND.blue, 0.6) : 'rgba(15,15,18,0.12)'}`,
    background: active ? '#EEF4FF' : 'white',
    color: active ? BRAND.blue : 'rgba(15,15,18,0.68)',
    fontWeight: 900,
    fontSize: 13,
    cursor: 'pointer',
  });
  const toolCategories = [
    { id: 'create', label: 'Create', icon: '+' },
    { id: 'layout', label: 'Layout', icon: '↱' },
    { id: 'aggregate', label: 'Aggregate', icon: '↔' },
    { id: 'route', label: 'Route', icon: 'R' },
    { id: 'export', label: 'Export', icon: '⇩' },
  ];
  const openCanvasToolSection = (sectionId) => {
    if (canvasToolbarCollapsed) {
      setActiveCanvasToolSection(sectionId);
      setCanvasToolbarCollapsed(false);
    }
  };
  const renderCanvasToolSection = (sectionId = activeCanvasToolSection) => {
    if (sectionId === 'layout') {
      return (
        <>
          <div style={toolSectionLabelStyle}>Layout</div>
          <div style={toolGridStyle}>
            <button type="button" onClick={ungroupSelectedNodes} disabled={!canUngroupSelected} title="Ungroup selected nodes" style={toolButtonStyle({ disabled: !canUngroupSelected, tone: BRAND.purple })}>↱</button>
            <button type="button" onClick={runCleanAndSpread} title="Auto arrange" style={toolButtonStyle({ active: true })}>⚡</button>
          </div>
        </>
      );
    }
    if (sectionId === 'aggregate') {
      return (
        <>
          <div style={toolSectionLabelStyle}>Aggregate</div>
          <div style={toolGridStyle}>
            <button type="button" onClick={toggleAggregateAllEdges} title={edgeAggregation.aggregateAll ? 'Expand directional edge bundles' : 'Aggregate repeated directional edges'} disabled={!multiEdgePairCount} style={toolButtonStyle({ active: edgeAggregation.aggregateAll, disabled: !multiEdgePairCount, tone: BRAND.purple })}>→</button>
            <button type="button" onClick={toggleHighlightedEdgePairAggregation} title="Aggregate or expand the selected directional pair" disabled={!canToggleHighlightedPair} style={toolButtonStyle({ active: highlightedPairAggregated, disabled: !canToggleHighlightedPair })}>⇄</button>
            <button type="button" onClick={toggleAggregateAllBidirectionalEdges} title={edgeAggregation.aggregateBidirectionalAll ? 'Expand all bidirectional bundles' : 'Aggregate all traffic between node pairs regardless of direction'} disabled={!multiBidirectionalEdgePairCount} style={toolButtonStyle({ active: edgeAggregation.aggregateBidirectionalAll, disabled: !multiBidirectionalEdgePairCount, tone: BRAND.purple })}>↔</button>
            <button type="button" onClick={toggleHighlightedBidirectionalEdgePairAggregation} title="Aggregate or expand selected bidirectional node pair" disabled={!canToggleHighlightedBidirectionalPair} style={toolButtonStyle({ active: highlightedBidirectionalPairAggregated, disabled: !canToggleHighlightedBidirectionalPair })}>⟷</button>
          </div>
        </>
      );
    }
    if (sectionId === 'route') {
      return (
        <>
          <div style={toolSectionLabelStyle}>Route</div>
          <div style={toolGridStyle}>
            <button type="button" onClick={() => setAllEdgeRoutingStyle(EDGE_ROUTING_STYLES.BEZIER)} title="Set all edges to Bezier routing" style={toolButtonStyle({ active: edgeRouting.defaultStyle === EDGE_ROUTING_STYLES.BEZIER && !Object.keys(edgeRouting.overrides || {}).length })}>B</button>
            <button type="button" onClick={() => setAllEdgeRoutingStyle(EDGE_ROUTING_STYLES.RECTANGULAR)} title="Set all edges to rectangular routing" style={toolButtonStyle({ active: edgeRouting.defaultStyle === EDGE_ROUTING_STYLES.RECTANGULAR && !Object.keys(edgeRouting.overrides || {}).length })}>R</button>
            <button type="button" onClick={toggleHighlightedEdgeRoutingStyle} title="Toggle routing for selected edge or bundle" disabled={!highlightedRoutingTargetKey} style={toolButtonStyle({ active: Boolean(highlightedRoutingTargetKey), disabled: !highlightedRoutingTargetKey, tone: BRAND.purple })}>{highlightedRoutingStyle === EDGE_ROUTING_STYLES.RECTANGULAR ? 'B' : 'R'}</button>
          </div>
        </>
      );
    }
    if (sectionId === 'export') {
      return (
        <>
          <div style={toolSectionLabelStyle}>Export</div>
          <div style={toolGridStyle}>
            <button type="button" onClick={exportDiagramXml} title="Export XML" style={toolButtonStyle({ active: true })}>X</button>
            <button type="button" onClick={exportDiagramJson} title="Export JSON" style={toolButtonStyle({ active: true })}>J</button>
          </div>
        </>
      );
    }
    return (
      <>
        <div style={toolSectionLabelStyle}>Create</div>
        <div style={toolGridStyle}>
          <button type="button" onClick={addManualDiagramNode} title="Add node" style={toolButtonStyle({ active: true })}>+</button>
          <button type="button" onClick={createGroupBox} title="Group selected nodes" style={toolButtonStyle({ active: true, tone: BRAND.purple })}>□</button>
          <button type="button" onClick={addNoteNode} title="Add note" style={toolButtonStyle({ tone: BRAND.yellow })}>📝</button>
        </div>
      </>
    );
  };
  const renderAllCanvasToolSections = () => (
    <>
      {toolCategories.map((category, index) => (
        <React.Fragment key={category.id}>
          {index > 0 && <div style={{ height: 1, background: 'rgba(15,15,18,0.08)', margin: '2px 0' }} />}
          {renderCanvasToolSection(category.id)}
        </React.Fragment>
      ))}
    </>
  );

  /* Render */
  return (
    <div ref={diagramHostRef} style={{ width: '100%', height: '100%', minHeight: 560, position: 'relative' }}>
      {/* 🧠 Canvas */}
      <div
        style={{
          border: `2px solid ${BRAND.blue}`,
          borderRadius: '8px',
          overflow: 'hidden',
          width: '100%',
          height: '100%',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 12,
            bottom: 12,
            left: 12,
            zIndex: 25,
            width: canvasToolbarCollapsed ? 36 : 128,
            pointerEvents: 'auto',
            transition: 'width 160ms ease',
          }}
        >
          <div
            style={{
              height: '100%',
              border: '1px solid rgba(15,15,18,0.12)',
              borderRadius: 10,
              background: 'rgba(255,255,255,0.96)',
              boxShadow: '0 12px 28px rgba(15,15,18,0.16)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <button
              type="button"
              onClick={() => setCanvasToolbarCollapsed((value) => !value)}
              title={canvasToolbarCollapsed ? 'Show canvas tools' : 'Hide canvas tools'}
              aria-label={canvasToolbarCollapsed ? 'Show canvas tools' : 'Hide canvas tools'}
              style={{
                height: 34,
                border: 0,
                borderBottom: canvasToolbarCollapsed ? 0 : '1px solid rgba(15,15,18,0.08)',
                background: canvasToolbarCollapsed ? BRAND.blue : '#F8FAFC',
                color: canvasToolbarCollapsed ? 'white' : BRAND.dark,
                fontWeight: 900,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              {canvasToolbarCollapsed ? '›' : '‹ Tools'}
            </button>
            <div
              style={{
                flex: '1 1 auto',
                minHeight: 0,
                overflowY: 'auto',
                padding: canvasToolbarCollapsed ? '8px 3px' : 8,
                display: 'grid',
                gap: 6,
                alignContent: 'start',
              }}
            >
              {canvasToolbarCollapsed ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 5,
                    justifyContent: 'center',
                  }}
                >
                  {toolCategories.map((category) => (
                    <button
                      key={category.id}
                      type="button"
                      title={category.label}
                      aria-label={category.label}
                      onClick={() => openCanvasToolSection(category.id)}
                      style={toolCategoryButtonStyle({ active: false })}
                    >
                      {category.icon}
                    </button>
                  ))}
                </div>
              ) : renderAllCanvasToolSections()}
            </div>
          </div>
        </div>
        <ReactFlow
          nodes={viewNodes}
          edges={viewEdges}
          onInit={(instance) => {
            setTimeout(() => {
              try {
                instance.fitView({ padding: 0.2, includeHiddenNodes: true });
              } catch {}
            }, 0);
          }}
          
          defaultEdgeOptions={{
            type: edgeTypeForRoutingStyle(edgeRouting.defaultStyle),
            markerEnd: { type: MarkerType.ArrowClosed, width: ARROW_SIZE, height: ARROW_SIZE, color: BRAND.blue },
          }}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeUpdateStart={onEdgeUpdateStart}
          onEdgeUpdate={onEdgeUpdate}
          onEdgeUpdateEnd={onEdgeUpdateEnd}
          onEdgeClick={(evt, edge) => setHighlightedEdgeId(edge.id)}
          onPaneClick={() => { setHighlightedEdgeId(null); setContextMenu(null); }}
          onPaneContextMenu={(event) => {
            event.preventDefault();
            setContextMenu(null);
          }}
          onNodeClick={() => { setHighlightedEdgeId(null); setContextMenu(null); }}
          onNodeContextMenu={(event, node) => {
            event.preventDefault();
            const selected = selectedNodeIds.length ? selectedNodeIds : [node.id];
            const eligible = selected.filter((id) => !id.startsWith('g:'));
            if (!eligible.length || !groupBoxes.length) return;
            const bounds = diagramHostRef.current?.getBoundingClientRect();
            setContextMenu({
              x: event.clientX - (bounds?.left || 0),
              y: event.clientY - (bounds?.top || 0),
              nodeIds: eligible,
            });
          }}
          onSelectionChange={({ nodes: selectedNodes }) => {
            setSelectedNodeIds((selectedNodes || []).map((n) => n.id));
          }}
          selectionOnDrag
          multiSelectionKeyCode={['Meta', 'Control', 'Shift']}
          nodesDraggable
          nodesConnectable
          edgesUpdatable
          connectionMode={ConnectionMode.Loose}
          onConnectStart={onConnectStartLoose}
          onConnectEnd={onConnectEndLoose}
          minZoom={THEME.canvas.minZoom}
          maxZoom={THEME.canvas.maxZoom}
          panOnScroll
          panOnScrollMode="free"
          zoomOnScroll={false}
          preventScrolling
          proOptions={{ hideAttribution: true }}
          onNodeDoubleClick={(event, node) => {
            event.preventDefault();
            event.stopPropagation();
            clearBrowserTextSelection();
            setEditModal({
              type: 'node',
              id: node.id,
              nodeType: node.type || 'bidirectional',
              label: node.data.label || '',
              description: node.data.description || '',
              color: node.data.brandColor || (node.type === 'groupBox' ? BRAND.purple : node.type === 'note' ? BRAND.yellow : BRAND.blue),
            });
            requestAnimationFrame(clearBrowserTextSelection);
          }}
          onEdgeDoubleClick={(event, edge) => {
            event.preventDefault();
            event.stopPropagation();
            clearBrowserTextSelection();
            setEditModal({
              type: 'edge',
              id: edge.id,
              label: edge.label || '',
              description: edge.data?.description || edge.data?.summary || '',
              data: edge.data || {},
              aggregated: Boolean(edge.data?.aggregated),
            });
            requestAnimationFrame(clearBrowserTextSelection);
          }}
          onDoubleClick={(event) => {
            const isInside = event.target.closest('.react-flow__node, .react-flow__edge, .react-flow__edge-label');
            if (isInside) return;
          
            const bounds = diagramHostRef.current?.getBoundingClientRect();
            const position = nearestFreePosition(
              { x: event.clientX - (bounds?.left || 0), y: event.clientY - (bounds?.top || 0) },
              getNodes()
            );
          
            const existing = collectExistingLabels(getNodes(), rows);
            const label = makeUniqueNewLabel(existing);   // guarantees unique "new: N"
            const rfId = nodeIdForFunction(label);
          
            const newNode = {
              id: rfId,
              type: 'bidirectional',
              position,
              data: { label, brandColor: BRAND.purple, brandTint: rgba(BRAND.purple, 0.08) },
            };
          
            setNodes((nds) => [...nds, newNode]);
            posRef.current.set(rfId, { position: newNode.position, parentId: null });
            persistSoon();
          }}
	          onNodeDragStart={(_, node) => {
	            if (node?.type === 'groupBox') {
	              const startBox = groupBoxesRef.current.find((box) => box.id === node.id);
	              if (!startBox) return;
	              groupDragRef.current = {
	                groupId: node.id,
	                startPosition: { ...startBox.position },
	              };
	              return;
	            }
	            if (node?.parentNode) {
	              childDragRef.current = {
	                nodeId: node.id,
	                parentId: node.parentNode,
	                position: { ...(node.position || { x: GROUP.padX, y: GROUP.padTop }) },
	              };
	            }
	          }}
	          onNodeDrag={(event, node) => {
	            if (node?.type === 'groupBox') {
	              const drag = groupDragRef.current;
	              if (!drag || drag.groupId !== node.id) return;
	              return;
	            }
	            expandGroupWhileDraggingChild(event, node);
	          }}
	          onNodeDragStop={(_, node) => {
	            if (node?.id && node?.position) {
              let finalParentId = node.parentNode || null;
              if (node.type === 'groupBox') {
                const activeGroupDrag = groupDragRef.current?.groupId === node.id
                  ? groupDragRef.current
                  : null;

                setGroupBoxes((currentBoxes) => {
                  const baseBoxes = groupBoxesRef.current.length ? groupBoxesRef.current : currentBoxes;
                  const nextBoxes = baseBoxes.map((box) => (
                    box.id === node.id ? { ...box, position: { ...node.position } } : box
                  ));
                  persistGroupsSoon(nextBoxes);
                  return nextBoxes;
                });

                if (activeGroupDrag) groupDragRef.current = null;
                setTimeout(() => refreshEdgesFromNodes(getNodes()), 0);
              } else {
                const currentNodes = getNodes();
                const byId = new Map(currentNodes.map((n) => [n.id, n]));
                const dragged = byId.get(node.id) || node;
                const abs = dragged.positionAbsolute || getNodeAbsolutePosition(dragged, byId);
                const containingBox = groupBoxes.find((box) => {
                  const left = box.position.x;
                  const top = box.position.y;
                  const right = left + (box.width || GROUP.w);
                  const bottom = top + (box.height || GROUP.h);
                  const cx = abs.x + THEME.node.w / 2;
                  const cy = abs.y + THEME.node.h / 2;
                  return cx >= left && cx <= right && cy >= top && cy <= bottom;
                });

                const keepCurrentGroup = dragged.parentNode && containingBox?.id === dragged.parentNode;
                const targetBox = keepCurrentGroup
                  ? groupBoxes.find((box) => box.id === dragged.parentNode)
                  : containingBox;

                if (targetBox) {
                  const relative = clampToGroup(
                    { x: abs.x - targetBox.position.x, y: abs.y - targetBox.position.y },
                    targetBox
                  );
                  finalParentId = targetBox.id;
                  setNodes((nds) => nds.map((n) => (
                    n.id === node.id ? { ...n, parentNode: targetBox.id, position: relative } : n
                  )));
                  posRef.current.set(node.id, { position: relative, parentId: targetBox.id });
                  if (!targetBox.autoGenerated) {
                    setTimeout(() => growGroupToFitMembers(targetBox.id, getNodes()), 0);
                  }
                } else if (dragged.parentNode) {
                  finalParentId = null;
                  setNodes((nds) => nds.map((n) => (
                    n.id === node.id ? { ...n, parentNode: undefined, position: abs } : n
                  )));
                  posRef.current.set(node.id, { position: abs, parentId: null });
                } else {
                  finalParentId = null;
                  posRef.current.set(node.id, { position: { x: abs.x, y: abs.y }, parentId: null });
                }
	              }
	              persistSoon();
	              savePositions(storageKey, posRef.current);
	              childDragRef.current = null;
	              if (node.type !== 'groupBox' && !finalParentId) {
	                nudgeIfOverlapping(node.id, nodes.filter((n) => !n.parentNode && n.type !== 'groupBox'), setNodes);
	              }
              setTimeout(() => {
                refreshEdgesFromNodes(getNodes());
              }, 0);
            }
          }}
        >
          <Background variant="dots" gap={18} size={1} />
        </ReactFlow>
      </div>

      {contextMenu && (
        <div
          style={{
            position: 'absolute',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 30,
            background: '#fff',
            border: '1px solid rgba(15,15,18,0.12)',
            borderRadius: 10,
            boxShadow: '0 10px 28px rgba(15,15,18,0.18)',
            minWidth: 220,
            overflow: 'hidden',
          }}
          onMouseLeave={() => setContextMenu(null)}
        >
          <div style={{ padding: '8px 10px', fontSize: 12, fontWeight: 700, borderBottom: '1px solid rgba(15,15,18,0.08)' }}>
            Add Selected Nodes To Group
          </div>
          {groupBoxes.map((box) => (
            <button
              key={box.id}
              onClick={() => assignNodesToGroup(contextMenu.nodeIds, box.id)}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', fontSize: 13, background: '#fff', border: 'none', cursor: 'pointer' }}
            >
              {box.label}
            </button>
          ))}
          <button
            onClick={() => ungroupNodes(contextMenu.nodeIds)}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', fontSize: 13, background: '#fff', border: 'none', borderTop: '1px solid rgba(15,15,18,0.08)', cursor: 'pointer' }}
          >
            Remove From Group
          </button>
        </div>
      )}

      {/* ✏️ Edit Modal */}
      {editModal && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 100,
            background: 'rgba(15,15,18,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) setEditModal(null);
          }}
        >
          <div
            style={{
              background: 'white',
              border: '1px solid rgba(0,0,0,0.1)',
              borderRadius: 12,
              width: 'min(860px, calc(100% - 32px))',
              maxHeight: 'calc(100% - 48px)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: 20,
                overflowY: 'auto',
                flex: '1 1 auto',
                minHeight: 0,
              }}
            >
            <h3 style={{ marginBottom: 10 }}>
              Edit {editModal.type === 'node' ? 'Node' : editModal.aggregated ? 'Aggregated Edge' : 'Edge'}
            </h3>
            <label style={{ display: 'block', marginBottom: 8 }}>
              Label:
              <input
                type="text"
                value={editModal.label}
                onChange={(e) => setEditModal((m) => ({ ...m, label: e.target.value }))}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', marginTop: 4, padding: 8, borderRadius: 8, border: '1px solid #ddd' }}
              />
            </label>
            <label style={{ display: 'block' }}>
              Description:
              <textarea
                value={editModal.description}
                onChange={(e) => setEditModal((m) => ({ ...m, description: e.target.value }))}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', marginTop: 4, padding: 8, borderRadius: 8, border: '1px solid #ddd' }}
                rows={4}
              />
            </label>
            {editModal.type === 'node' && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 14, marginBottom: 6 }}>Color:</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                  {COLOR_PRESETS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setEditModal((m) => ({ ...m, color }))}
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        border: editModal.color === color ? `2px solid ${BRAND.dark}` : '1px solid rgba(15,15,18,0.15)',
                        background: color,
                        cursor: 'pointer',
                        boxShadow: editModal.color === color ? '0 0 0 2px rgba(15,15,18,0.08)' : 'none',
                      }}
                      aria-label={`Choose color ${color}`}
                    />
                  ))}
                </div>
                <input
                  type="color"
                  value={editModal.color || BRAND.blue}
                  onChange={(e) => setEditModal((m) => ({ ...m, color: e.target.value }))}
                  style={{ display: 'block', width: 56, height: 36, padding: 0, border: '1px solid #ddd', borderRadius: 8, background: '#fff' }}
                />
              </div>
            )}
            <div
              style={{
                marginTop: 16,
                borderTop: '1px solid rgba(15,15,18,0.1)',
                paddingTop: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: BRAND.dark }}>
                  Associated Hazard Analysis
                </div>
                {hazardDataRows.length > 0 && (
                  <div style={{ fontSize: 12, color: 'rgba(15,15,18,0.58)' }}>
                    {editHazardRows.length} result{editHazardRows.length === 1 ? '' : 's'}
                  </div>
                )}
              </div>
              {!hazardDataRows.length ? (
                <div style={{ fontSize: 13, color: 'rgba(15,15,18,0.58)' }}>
                  Run hazard analysis to see linked results here.
                </div>
              ) : editHazardRows.length === 0 ? (
                <div style={{ fontSize: 13, color: 'rgba(15,15,18,0.58)' }}>
                  No hazard analysis rows are linked to this {editModal.type}.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {editHazardRows.map(({ sourceIndex, cells }) => (
                    <details
                      key={`${sourceIndex}-${cells.join('|')}`}
                      style={{
                        border: '1px solid rgba(45,125,254,0.18)',
                        borderRadius: 8,
                        background: '#F8FAFC',
                        overflow: 'hidden',
                      }}
                    >
                      <summary
                        style={{
                          cursor: 'pointer',
                          listStyle: 'none',
                          padding: '8px 10px',
                          background: '#EEF4FF',
                          color: '#0B3EA8',
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        <span>Source Row {sourceIndex + 1}: {getHazardCardTitle(cells, sourceIndex)}</span>
                        {typeof onOpenHazardRow === 'function' && (
                          <button
                            type="button"
                            onClick={() => onOpenHazardRow(sourceIndex)}
                            style={{
                              float: 'right',
                              border: 'none',
                              background: 'transparent',
                              color: BRAND.blue,
                              fontSize: 12,
                              fontWeight: 700,
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          >
                            Open in table
                          </button>
                        )}
                      </summary>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(120px, 180px) minmax(0, 1fr)',
                          gap: 0,
                        }}
                      >
                        {cells.map((cell, idx) => (
                          <React.Fragment key={`${sourceIndex}-${idx}`}>
                            <div
                              style={{
                                padding: '8px 10px',
                                borderTop: '1px solid rgba(15,15,18,0.08)',
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#4B5563',
                                background: 'white',
                              }}
                            >
                              {hazardHeaders[idx] || `Column ${idx + 1}`}
                            </div>
                            <div
                              style={{
                                padding: '8px 10px',
                                borderTop: '1px solid rgba(15,15,18,0.08)',
                                fontSize: 12,
                                color: '#111827',
                                background: 'white',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {String(cell ?? '') || '—'}
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </div>
            <div
              style={{
                marginTop: 16,
                borderTop: '1px solid rgba(15,15,18,0.1)',
                paddingTop: 14,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: BRAND.dark }}>
                  Associated Safety Issues
                </div>
                <div style={{ fontSize: 12, color: 'rgba(15,15,18,0.58)' }}>
                  {editSafetyIssues.length} issue{editSafetyIssues.length === 1 ? '' : 's'}
                </div>
              </div>
              {!Array.isArray(riskRegister) || !riskRegister.length ? (
                <div style={{ fontSize: 13, color: 'rgba(15,15,18,0.58)' }}>
                  Generate the risk assessment to see consolidated safety issues here.
                </div>
              ) : editSafetyIssues.length === 0 ? (
                <div style={{ fontSize: 13, color: 'rgba(15,15,18,0.58)' }}>
                  No consolidated safety issues are linked to this {editModal.aggregated ? 'aggregated edge' : editModal.type}.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {editSafetyIssues.map((issue, index) => (
                    <details
                      key={issue.id || `${issue.title}-${index}`}
                      style={{
                        border: '1px solid rgba(122,55,255,0.18)',
                        borderRadius: 8,
                        background: '#F8FAFC',
                        overflow: 'hidden',
                      }}
                    >
                      <summary
                        style={{
                          cursor: 'pointer',
                          listStyle: 'none',
                          padding: '8px 10px',
                          background: '#F5F0FF',
                          color: '#4C1D95',
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        <span>{issue.priority || 'P3+'} · {issue.title || `Safety Issue ${index + 1}`}</span>
                        <span style={{ float: 'right', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                          {typeof onOpenSafetyIssue === 'function' && (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                onOpenSafetyIssue(issue);
                                setEditModal(null);
                              }}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                color: BRAND.purple,
                                fontSize: 12,
                                fontWeight: 700,
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                padding: 0,
                              }}
                            >
                              Open issue
                            </button>
                          )}
                          <span style={{ color: 'rgba(76,29,149,0.72)', fontWeight: 600 }}>
                            Score {issue.score || 0}
                          </span>
                        </span>
                      </summary>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(140px, 190px) minmax(0, 1fr)',
                          gap: 0,
                        }}
                      >
                        {[
                          ['Description', issue.description],
                          ['Status', issue.status || 'Open'],
                          ['Owner', issue.owner || 'Unassigned'],
                          ['Due Date', issue.dueDate || 'Not set'],
                          ['Likelihood', issue.likelihood],
                          ['Severity', issue.severity],
                          ['Source Rows', issue.sourceIndexes?.length ? issue.sourceIndexes.map((sourceIndex) => `Source Row ${sourceIndex}`).join(', ') : 'Not linked'],
                        ].map(([label, value]) => (
                          <React.Fragment key={`${issue.id || index}-${label}`}>
                            <div
                              style={{
                                padding: '8px 10px',
                                borderTop: '1px solid rgba(15,15,18,0.08)',
                                fontSize: 12,
                                fontWeight: 700,
                                color: '#4B5563',
                                background: 'white',
                              }}
                            >
                              {label}
                            </div>
                            <div
                              style={{
                                padding: '8px 10px',
                                borderTop: '1px solid rgba(15,15,18,0.08)',
                                fontSize: 12,
                                color: '#111827',
                                background: 'white',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                              }}
                            >
                              {String(value ?? '') || '—'}
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </div>
            </div>
            <div
              style={{
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                padding: '14px 20px',
                borderTop: '1px solid rgba(15,15,18,0.1)',
                background: 'white',
                boxShadow: '0 -8px 18px rgba(15,15,18,0.04)',
              }}
            >
              <button onClick={() => setEditModal(null)} style={{ padding: '6px 12px', borderRadius: 8 }}>
                Cancel
              </button>
              <button
	                onClick={() => {
	                if (editModal.type === 'node') {
                    const nodeColor = editModal.color || BRAND.blue;
                    const nodeTint =
                      editModal.nodeType === 'note' ? rgba(nodeColor, 0.2) : rgba(nodeColor, 0.08);
		                  if (editModal.id.startsWith('g:')) {
	                    setGroupBoxes((currentBoxes) => {
                        const baseBoxes = groupBoxesRef.current.length ? groupBoxesRef.current : currentBoxes;
	                      const nextBoxes = baseBoxes.map((box) => (
	                        box.id === editModal.id
                            ? {
                                ...box,
                                label: editModal.label,
                                description: editModal.description,
                                descriptionUserEdited: true,
                                brandColor: nodeColor,
                              }
                            : box
	                      ));
	                      persistGroupsSoon(nextBoxes);
	                      return nextBoxes;
	                    });
	                    setNodes((nds) =>
	                      nds.map((n) =>
	                        n.id === editModal.id
	                          ? { ...n, data: { ...n.data, label: editModal.label, description: editModal.description, brandColor: nodeColor } }
	                          : n
	                      )
	                    );
	                    setEditModal(null);
	                    return;
	                  }
                    if (editModal.id.startsWith('note:') || editModal.nodeType === 'note') {
                      setNodes((nds) =>
                        nds.map((n) =>
                          n.id === editModal.id
                            ? {
                                ...n,
                                data: {
                                  ...n.data,
                                  label: editModal.label,
                                  description: editModal.description,
                                  brandColor: nodeColor,
                                  brandTint: nodeTint,
                                },
                              }
                            : n
                        )
                      );
                      setEditModal(null);
                      return;
                    }
	                  setNodes((nds) =>
	                    nds.map((n) =>
	                      n.id === editModal.id
	                        ? {
                              ...n,
                              data: {
                                ...n.data,
                                label: editModal.label,
                                description: editModal.description,
                                brandColor: nodeColor,
                                brandTint: nodeTint,
                              },
                            }
	                        : n
	                    )
                  );

                  const updatedRows = rows.map((r) => {
                    if (nodeIdForFunction(rowFromFunction(r)) === editModal.id)
                      return { ...r, fromFunction: normalizeFunctionName(editModal.label), fromDetails: editModal.description };
                    if (nodeIdForFunction(rowToFunction(r)) === editModal.id)
                      return { ...r, toFunction: normalizeFunctionName(editModal.label), toDetails: editModal.description };
                    return r;
                  });

                  const oldId = editModal.id;
                  const newId = nodeIdForFunction(editModal.label);
                  const oldPos = posRef.current.get(oldId);
                  if (oldPos && newId) {
                    posRef.current.set(newId, { ...oldPos });
                    posRef.current.delete(oldId);
                    persistSoon();
                  }

                  onUpdateRows?.(updatedRows);
                } else {
                  setEdges((eds) =>
                    eds.map((e) =>
                      e.id === editModal.id
                        ? { ...e, label: editModal.label, data: { ...e.data, description: editModal.description } }
                        : e
                    )
                  );
                  const updatedRows = rows.map((r, i) => {
                    const edgeId = edgeIdForRow(r, i);
                    if (edgeId !== editModal.id) return r;
                    return { ...r, controlAction: editModal.label, controlDetails: editModal.description };
                  });
                  onUpdateRows?.(updatedRows);
                }
                  setEditModal(null);
                }}
                style={{
                  background: BRAND.purple,
                  color: 'white',
                  padding: '8px 14px',
                  borderRadius: 8,
                  fontWeight: 700,
                  boxShadow: '0 6px 16px rgba(122,55,255,0.18)',
                }}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default forwardRef(function LiteSummaryDiagramReactFlow(props, ref) {
  return (
    <ReactFlowProvider>
      <DiagramBody ref={ref} {...props} />
    </ReactFlowProvider>
  );
});

export const __testables = {
  buildFunctionalDiagramRenderState,
  buildExplicitAutoGroupAssignmentMap,
  findOpenCanvasPosition,
  groupBoxListsEqual,
  recoverGroupBoxesFromRenderedNodes,
  placeMissingFunctionalNodesIncrementally,
  rectsOverlap,
  nodeRectFromPosition,
  rowsToRawEdges,
};

/* ================================
 * Small helper
 * ================================ */
function cryptoId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `id_${Math.random().toString(36).slice(2, 10)}`;
}
